/**
 * Universal SaaS Auto-Trading Platform — User Dashboard
 * Default route: /algotrading
 * MVP Phase 1: API Vault + Strategies + Paper Trading + Subscriptions + Affiliate Network + Disclaimers
 */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Activity, Zap, ShieldCheck, Cpu, TrendingUp, Layers, Key,
  AlertTriangle, RefreshCw, Trash2, Play, Pause, Wallet,
  Users, Award, ChevronRight, Lock, Unlock, BookOpen, BarChart3,
  Trophy, Crown, Settings as SettingsIcon, Brain, Sparkles, ExternalLink,
  Radio, LineChart, IndianRupee, ShieldAlert, CheckCircle2, AlertCircle, Clock
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import BrokerSetupWizard from '../../components/BrokerSetupWizard';
import PushSubscribeButton from '../../components/PushSubscribeButton';
import TradingTerminal from '../../components/TradingTerminal';
import { Link } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

const AlgoTrading = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('terminal');
  const [status, setStatus] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [mySubscription, setMySubscription] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [bots, setBots] = useState([]);
  const [paperWallet, setPaperWallet] = useState(null);
  const [paperTrades, setPaperTrades] = useState([]);
  const [mlmSummary, setMlmSummary] = useState(null);
  const [mlmNetwork, setMlmNetwork] = useState(null);
  const [fnoAccess, setFnoAccess] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [liveReadiness, setLiveReadiness] = useState(null);
  const [liveOrders, setLiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [consentData, setConsentData] = useState(null);
  // Real-time ticker (WebSocket)
  const ticker = useTickerStream(true);

  const TABS = [
    { id: 'terminal', label: '⚡ Trading Terminal', icon: Activity },
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'smart-signals', label: '🎯 AI Smart Signals', icon: Sparkles },
    { id: 'matchmaker', label: '🧠 AI Strategy Match', icon: Brain },
    { id: 'strategies', label: 'Strategies', icon: Cpu },
    { id: 'bots', label: 'My Bots', icon: Zap },
    { id: 'paper', label: 'Paper Trading', icon: TrendingUp },
    { id: 'live', label: '🔴 LIVE Trading', icon: Zap },
    { id: 'live-dashboard', label: '📊 Live Broker Dashboard', icon: Activity },
    { id: 'profit-share', label: '💰 Profit-Share', icon: IndianRupee },
    { id: 'backtest', label: 'Backtest', icon: LineChart },
    { id: 'fno', label: 'F&O Desk', icon: Crown },
    { id: 'ai-coach', label: 'AI Coach', icon: Brain },
    { id: 'education', label: '🎓 Trading Education', icon: GraduationCap },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'apikeys', label: 'API Vault', icon: Key },
    { id: 'plans', label: 'Subscription', icon: Award },
    { id: 'deposit', label: 'Deposit Cash', icon: Wallet },
    { id: 'affiliate', label: 'Affiliate (Affiliate Network)', icon: Users },
  ];

  useEffect(() => {
    loadAll();
    checkConsent();
    // Auto-refresh paper wallet + prices every 20s
    const id = setInterval(() => {
      if (activeTab === 'paper') loadPaperData();
      if (activeTab === 'overview') loadStatus();
    }, 20000);
    return () => clearInterval(id);
  }, [activeTab]);

  const checkConsent = async () => {
    try {
      const r = await api.get('/algotrading/consent/text');
      if (!r.data.already_accepted) {
        setConsentData(r.data);
        setShowConsent(true);
      }
    } catch (e) { /* ignore */ }
  };

  const [refreshDue, setRefreshDue] = useState(null);
  const checkTokenRefresh = async () => {
    try {
      const r = await api.get('/algotrading/token-refresh-due');
      setRefreshDue(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { checkTokenRefresh(); }, []);

  const acceptConsent = async () => {
    try {
      await api.post('/algotrading/consent/accept', {
        consent_version: consentData?.consent_version || 'v1.0',
        user_agent: navigator.userAgent,
      });
      setShowConsent(false);
      toast.success('Consent accepted ✓');
    } catch (e) { toast.error('Could not save consent'); }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStatus(),
        loadStrategies(),
        loadStocks(),
        loadTiers(),
        loadMySubscription(),
        loadApiKeys(),
        loadBrokers(),
        loadBots(),
        loadPaperData(),
        loadMLM(),
        loadFnoAccess(),
        loadLeaderboard(),
        loadWalletBalance(),
        loadTrialStatus(),
        loadLiveReadiness(),
        loadLiveOrders(),
      ]);
    } catch (e) {
      console.error('algotrading load failed', e);
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try { const r = await api.get('/algotrading/status'); setStatus(r.data); } catch (e) { console.warn(e?.message); }
  };
  const loadStrategies = async () => {
    try { const r = await api.get('/algotrading/strategies'); setStrategies(r.data.strategies || []); } catch (e) { console.warn(e?.message); }
  };
  const loadStocks = async () => {
    try { const r = await api.get('/algotrading/stocks'); setStocks(r.data.stocks || []); } catch (e) { console.warn(e?.message); }
  };
  const loadTiers = async () => {
    try { const r = await api.get('/algotrading/subscriptions/tiers'); setTiers(r.data.tiers || []); } catch (e) { console.warn(e?.message); }
  };
  const loadMySubscription = async () => {
    try { const r = await api.get('/algotrading/subscriptions/me'); setMySubscription(r.data.subscription); } catch (e) { console.warn(e?.message); }
  };
  const loadApiKeys = async () => {
    try { const r = await api.get('/algotrading/api-keys'); setApiKeys(r.data.keys || []); } catch (e) { console.warn(e?.message); }
  };
  const loadBrokers = async () => {
    try { const r = await api.get('/algotrading/brokers'); setBrokers(r.data.brokers || []); } catch (e) { console.warn(e?.message); }
  };
  const loadBots = async () => {
    try { const r = await api.get('/algotrading/bots'); setBots(r.data.bots || []); } catch (e) { console.warn(e?.message); }
  };
  const loadPaperData = async () => {
    try {
      const [w, t] = await Promise.all([
        api.get('/algotrading/paper/wallet'),
        api.get('/algotrading/paper/trades?limit=30'),
      ]);
      setPaperWallet(w.data);
      setPaperTrades(t.data.trades || []);
    } catch (e) { console.warn(e?.message); }
  };
  const loadMLM = async () => {
    try {
      const [s, n] = await Promise.all([
        api.get('/algotrading/mlm/summary'),
        api.get('/algotrading/mlm/network'),
      ]);
      setMlmSummary(s.data);
      setMlmNetwork(n.data);
    } catch (e) { console.warn(e?.message); }
  };
  const loadFnoAccess = async () => {
    try { const r = await api.get('/algotrading/fno/access'); setFnoAccess(!!r.data.has_fno_access); } catch (e) { console.warn(e?.message); }
  };
  const loadLeaderboard = async () => {
    try { const r = await api.get('/algotrading/leaderboard?limit=50'); setLeaderboard(r.data); } catch (e) { console.warn(e?.message); }
  };
  const loadWalletBalance = async () => {
    try { const r = await api.get('/algotrading/wallet-balance'); setWalletBalance(r.data); } catch (e) { console.warn(e?.message); }
  };
  const loadTrialStatus = async () => {
    try { const r = await api.get('/algotrading/subscriptions/trial-status'); setTrialStatus(r.data); } catch (e) { console.warn(e?.message); }
  };
  const loadLiveReadiness = async () => {
    try { const r = await api.get('/algotrading/live-readiness'); setLiveReadiness(r.data); } catch (e) { console.warn(e?.message); }
  };
  const loadLiveOrders = async () => {
    try { const r = await api.get('/algotrading/live/orders?limit=30'); setLiveOrders(r.data.orders || []); } catch (e) { console.warn(e?.message); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <div className="text-white">Loading Anant Smart Trading Tools…</div>
        </div>
      </div>
    );
  }

  if (!status?.module_enabled) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950">
        <div className="max-w-md mx-auto bg-slate-900/70 backdrop-blur border border-amber-500/30 rounded-2xl p-8 text-center mt-12">
          <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Module Disabled</h2>
          <p className="text-slate-400">Auto-Trading is currently disabled by admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="algotrading-page">
      <div className="max-w-7xl mx-auto">

        {/* Kill-switch banner */}
        {status?.kill_switch && (
          <div className="mb-6 bg-red-600/30 border-2 border-red-500 rounded-xl p-4 flex items-center gap-3" data-testid="kill-switch-banner">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <div className="flex-1">
              <div className="font-bold text-red-200">EMERGENCY KILL-SWITCH ACTIVE</div>
              <div className="text-sm text-red-300">All bots halted. Reason: {status.kill_switch_reason || 'Emergency'}</div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6">
          <div>
            <div className="text-amber-400 text-sm font-semibold uppercase tracking-wider mb-1">Premium · AI-Powered</div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-400 to-pink-500 bg-clip-text text-transparent">
              ANANT SMART TRADING TOOLS &amp; SERVICES
            </h1>
            <p className="text-slate-400 mt-1 sm:mt-2 text-xs sm:text-sm">
              9 Algorithmic Strategies (7 base + 2 F&amp;O Pro) · AI Smart Signals · Paper &amp; Live · Stocks · Crypto · Forex · Options
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3 flex-wrap">
            <PushSubscribeButton compact={false} />
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 ${
              ticker.status === 'connected' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' :
              'bg-slate-500/20 text-slate-300 border border-slate-500/40'
            }`} data-testid="live-ws-status">
              <Radio className={`w-3 h-3 ${ticker.status === 'connected' ? 'animate-pulse' : ''}`} />
              {ticker.status === 'connected' ? 'LIVE TICKER' : 'CONNECTING…'}
            </div>
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${status?.live_trading_enabled ? 'bg-green-500/20 text-green-300 border border-green-500/40' : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'}`}>
              {status?.live_trading_enabled ? '● LIVE TRADING ENABLED' : '● PAPER MODE ONLY'}
            </div>
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${mySubscription ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-slate-500/20 text-slate-300 border border-slate-500/40'}`}>
              {mySubscription ? `✓ ${mySubscription.tier_name}` : 'No active plan'}
            </div>
          </div>
        </div>

        {/* Daily 8:30 AM IST Zerodha token refresh prompt */}
        {refreshDue?.refresh_due && (
          <div className="mb-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-400/50 rounded-2xl p-4 flex items-center gap-3" data-testid="token-refresh-banner">
            <Clock className="w-6 h-6 text-amber-300 flex-shrink-0 animate-pulse" />
            <div className="flex-1">
              <div className="text-amber-200 font-bold">⏰ Daily Zerodha Token Refresh Required</div>
              <div className="text-amber-100/70 text-xs">It&apos;s past 8:30 AM IST. Your broker access token has expired (Zerodha tokens are daily). Please refresh to enable live trading today.</div>
            </div>
            <button onClick={() => setActiveTab('apikeys')} data-testid="token-refresh-cta" className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-bold whitespace-nowrap">
              Refresh Now →
            </button>
          </div>
        )}

        {/* SEBI Disclaimer */}
        <div className="mb-4 bg-slate-900/50 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-200/80">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{status?.legal_disclaimer || 'Tool Provider Only — Not a Financial Advisor.'}</span>
          </div>
        </div>

        {/* Wallet Strip — always visible */}
        {walletBalance && (
          <div className="mb-4 grid grid-cols-3 gap-2 md:gap-3 bg-gradient-to-r from-emerald-500/10 via-amber-500/10 to-pink-500/10 border border-white/10 rounded-2xl p-3" data-testid="algo-wallet-strip">
            <div className="text-center" data-testid="wallet-cash">
              <div className="text-[10px] text-emerald-300 uppercase tracking-wider font-semibold">Cash Wallet</div>
              <div className="text-base md:text-xl font-extrabold text-emerald-200 break-all">₹{walletBalance.cash_balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
            <div className="text-center border-x border-white/10" data-testid="wallet-tsb">
              <div className="text-[10px] text-amber-300 uppercase tracking-wider font-semibold">TSB Wallet</div>
              <div className="text-base md:text-xl font-extrabold text-amber-200 break-all">{walletBalance.tsb_balance.toLocaleString('en-IN', { maximumFractionDigits: 4 })} TSB</div>
              <div className="text-[10px] text-slate-400">= ₹{walletBalance.tsb_value_inr.toLocaleString('en-IN', { maximumFractionDigits: 2 })} @ {walletBalance.tsb_rate.toFixed(2)}</div>
            </div>
            <div className="text-center" data-testid="wallet-total">
              <div className="text-[10px] text-pink-300 uppercase tracking-wider font-semibold">Total (INR)</div>
              <div className="text-base md:text-xl font-extrabold text-pink-200 break-all">₹{walletBalance.total_inr_equivalent.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="relative mb-6 sticky top-20 z-30">
          <div className="flex gap-2 overflow-x-auto pb-2 px-1 -mx-1 bg-gradient-to-b from-slate-950/95 to-transparent backdrop-blur scroll-smooth snap-x snap-mandatory" style={{ scrollbarWidth: 'thin' }}>
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`algo-tab-${tab.id}`}
                  title={tab.label}
                  className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium whitespace-nowrap text-xs sm:text-sm transition snap-start flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                      : 'bg-white/5 hover:bg-white/10 text-slate-300'
                  }`}
                >
                  <Icon className="w-4 h-4 sm:w-4 sm:h-4" />
                  <span className="max-[359px]:hidden">{tab.label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade hint to indicate horizontal scroll on mobile */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-slate-950 via-slate-950/70 to-transparent sm:hidden" />
          <div className="text-[9px] text-amber-300/40 text-center mt-0.5 sm:hidden flex items-center justify-center gap-1">
            <ChevronRight className="w-3 h-3 inline" /> swipe for more tabs
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'terminal' && (
          <TradingTerminal
            ticker={ticker}
            bots={bots}
            brokers={brokers}
            status={status}
            walletBalance={walletBalance}
            mySubscription={mySubscription}
            liveReadiness={liveReadiness}
            setActiveTab={setActiveTab}
            reloadBots={loadBots}
          />
        )}
        {activeTab === 'education' && (
          <EducationRedirectCard />
        )}
        {activeTab === 'overview' && (
          <OverviewTab
            status={status} mySubscription={mySubscription}
            paperWallet={paperWallet} mlmSummary={mlmSummary}
            bots={bots} setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'smart-signals' && (
          <SmartSignalsTab mySubscription={mySubscription} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'matchmaker' && (
          <StrategyMatchmakerTab />
        )}
        {activeTab === 'live-dashboard' && (
          <LiveBrokerDashboardTab />
        )}
        {activeTab === 'profit-share' && (
          <ProfitShareTab paperWallet={paperWallet} />
        )}
        {activeTab === 'strategies' && (
          <StrategiesTab strategies={strategies} onCreateBot={loadBots} mySubscription={mySubscription} status={status} fnoAccess={fnoAccess} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'bots' && (
          <BotsTab bots={bots} strategies={strategies} stocks={stocks} reload={loadBots} reloadPaper={loadPaperData} fnoAccess={fnoAccess} />
        )}
        {activeTab === 'paper' && (
          <PaperTab paperWallet={paperWallet} paperTrades={paperTrades} stocks={stocks} bots={bots} reload={loadPaperData} />
        )}
        {activeTab === 'backtest' && (
          <BacktestTab strategies={strategies} stocks={stocks} />
        )}
        {activeTab === 'fno' && (
          <FnoDeskTab fnoAccess={fnoAccess} bots={bots} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'ai-coach' && (
          <AICoachTab fnoAccess={fnoAccess} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'leaderboard' && (
          <LeaderboardTab data={leaderboard} reload={loadLeaderboard} />
        )}
        {activeTab === 'apikeys' && (
          <ApiVaultTab apiKeys={apiKeys} brokers={brokers} reload={loadApiKeys} />
        )}
        {activeTab === 'plans' && (
          <PlansTab tiers={tiers} mySubscription={mySubscription} walletBalance={walletBalance} trialStatus={trialStatus} reload={() => { loadMySubscription(); loadWalletBalance(); loadTrialStatus(); }} />
        )}
        {activeTab === 'live' && (
          <LiveTradingTab
            readiness={liveReadiness}
            bots={bots}
            orders={liveOrders}
            apiKeys={apiKeys}
            setActiveTab={setActiveTab}
            reload={() => { loadBots(); loadLiveReadiness(); loadLiveOrders(); }}
          />
        )}
        {activeTab === 'deposit' && (
          <DepositTab walletBalance={walletBalance} reload={loadWalletBalance} />
        )}
        {activeTab === 'affiliate' && (
          <AffiliateTab summary={mlmSummary} network={mlmNetwork} status={status} />
        )}
      </div>

      {/* Clickwrap Consent Popup */}
      {showConsent && consentData && (
        <ConsentModal data={consentData} onAccept={acceptConsent} />
      )}
    </div>
  );
};

// ============ Education Redirect Card (intra-tab CTA) ============
const EducationRedirectCard = () => (
  <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-950/60 via-slate-950/80 to-slate-950 p-8 text-center relative overflow-hidden" data-testid="education-redirect-card">
    <div className="hidden md:block absolute -top-20 -right-20 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl" />
    <div className="hidden md:block absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-violet-500/20 blur-3xl" />
    <div className="relative">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 text-xs font-bold tracking-widest uppercase mb-4">
        <GraduationCap className="w-4 h-4" /> Anant Trading Academy
      </div>
      <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-indigo-300 via-violet-300 to-pink-300 bg-clip-text text-transparent">
        Learn Trading the Smart Way
      </h2>
      <p className="text-slate-300/80 max-w-2xl mx-auto mt-3">
        Structured lessons from Basics to F&amp;O · AI Trading Tutor chatbot · Quizzes &amp; Certifications ·
        Earn TSB tokens on every completed lesson · 7-day &amp; 30-day learning streak bonuses.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/education"
          data-testid="education-redirect-cta"
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 text-white font-bold shadow-lg shadow-indigo-500/30 flex items-center gap-2"
        >
          <BookOpen className="w-5 h-5" /> Open Education Hub
        </Link>
        <Link
          to="/algotrading"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold flex items-center gap-2"
          data-testid="education-back-to-terminal"
        >
          <Activity className="w-5 h-5" /> Back to Terminal
        </Link>
      </div>
    </div>
  </div>
);

// ============ Overview ============
const OverviewTab = ({ status, mySubscription, paperWallet, mlmSummary, bots, setActiveTab }) => {
  const stats = [
    { label: 'Paper Portfolio', value: `₹${(paperWallet?.portfolio_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: Wallet, color: 'from-emerald-500 to-teal-500' },
    { label: 'Realized P&L', value: `₹${(paperWallet?.wallet?.realized_pnl || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: TrendingUp, color: 'from-blue-500 to-indigo-500' },
    { label: 'Active Bots', value: bots.filter(b => b.is_active).length, icon: Cpu, color: 'from-amber-500 to-orange-500' },
    { label: 'Affiliate Network Earnings', value: `₹${(mlmSummary?.total_earnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: Award, color: 'from-pink-500 to-rose-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 hover:border-amber-500/30 transition">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-2xl font-bold">{s.value}</div>
              <div className="text-xs text-slate-400 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* CTAs */}
      <div className="grid md:grid-cols-3 gap-4">
        <button onClick={() => setActiveTab('strategies')} className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-5 text-left hover:border-amber-500 transition" data-testid="cta-explore-strategies">
          <Cpu className="w-8 h-8 text-amber-400 mb-3" />
          <div className="text-lg font-bold">Explore 7 Strategies</div>
          <div className="text-sm text-slate-400 mt-1">Trend Rider, Mean Reversion, VWAP, Grid Matrix, MACD…</div>
        </button>
        <button onClick={() => setActiveTab('paper')} className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-2xl p-5 text-left hover:border-emerald-500 transition" data-testid="cta-paper-trading">
          <TrendingUp className="w-8 h-8 text-emerald-400 mb-3" />
          <div className="text-lg font-bold">Paper Trade Now</div>
          <div className="text-sm text-slate-400 mt-1">₹1 Crore virtual capital · 25 Indian stocks · risk-free</div>
        </button>
        <button onClick={() => setActiveTab('plans')} className="bg-gradient-to-br from-pink-500/20 to-rose-500/20 border border-pink-500/30 rounded-2xl p-5 text-left hover:border-pink-500 transition" data-testid="cta-upgrade-plan">
          <Award className="w-8 h-8 text-pink-400 mb-3" />
          <div className="text-lg font-bold">{mySubscription ? 'Renew Subscription' : 'Choose a Plan'}</div>
          <div className="text-sm text-slate-400 mt-1">50% Cash + 50% TSB · Affiliate 20%+10%</div>
        </button>
      </div>

      {/* Mode warning */}
      {!status?.live_trading_enabled && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 flex items-start gap-3" data-testid="paper-mode-notice">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <div className="font-bold text-amber-300">Phase 1 — Paper Mode Active</div>
            <div className="text-sm text-amber-200/80 mt-1">
              Live broker execution (Binance / Zerodha / MT5) is locked. All trades run in the ₹1 Cr paper simulator. Educational only — no real capital at risk.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Strategies ============
const StrategiesTab = ({ strategies, onCreateBot, mySubscription, status, fnoAccess, setActiveTab }) => {
  const [creating, setCreating] = useState(null);
  const [fnoConfig, setFnoConfig] = useState(null); // shows F&O risk-config modal when set

  const handleQuickStart = async (strat) => {
    if (strat.fno_only && !fnoAccess) {
      toast.error('F&O Pro-Plan (₹5,000/mo) required to unlock this strategy');
      return;
    }
    if (strat.fno_only) {
      // Open F&O risk-config modal first
      setFnoConfig({
        strategy: strat,
        fno_lots: 1,
        fno_daily_loss_limit: 5000,
        fno_daily_profit_target: 10000,
        fno_max_trades_per_day: 4,
        fno_slippage_control: true,
        instrument: 'BANKNIFTY',
      });
      return;
    }
    setCreating(strat.id);
    try {
      await api.post('/algotrading/bots', {
        strategy_id: strat.id,
        mode: 'paper',
        asset_class: 'stocks',
        capital_allocated: 200000,
      });
      toast.success(`Bot created for ${strat.name} (Paper mode)`);
      onCreateBot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create bot');
    } finally {
      setCreating(null);
    }
  };

  const handleFnoConfirm = async () => {
    if (!fnoConfig) return;
    const strat = fnoConfig.strategy;
    setCreating(strat.id);
    try {
      await api.post('/algotrading/bots', {
        strategy_id: strat.id,
        mode: 'paper',
        asset_class: 'fno',
        capital_allocated: 500000,
        custom_params: { instrument: fnoConfig.instrument },
        fno_lots: parseInt(fnoConfig.fno_lots, 10),
        fno_daily_loss_limit: parseFloat(fnoConfig.fno_daily_loss_limit) || null,
        fno_daily_profit_target: parseFloat(fnoConfig.fno_daily_profit_target) || null,
        fno_max_trades_per_day: parseInt(fnoConfig.fno_max_trades_per_day, 10) || null,
        fno_slippage_control: !!fnoConfig.fno_slippage_control,
      });
      toast.success(`${strat.name} F&O bot deployed with risk gates`);
      setFnoConfig(null);
      onCreateBot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deploy F&O bot');
    } finally { setCreating(null); }
  };

  return (
    <>
      {!fnoAccess && (
        <div className="mb-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-pink-500/30 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="fno-locked-banner">
          <div>
            <div className="font-bold text-pink-300 flex items-center gap-2"><Crown className="w-4 h-4" /> F&O Strategies Locked</div>
            <div className="text-sm text-slate-300 mt-1">Unlock 9:20 Delta Neutral & Gamma Burst with the <b>F&O Pro-Plan (₹5,000/mo)</b>. Includes advanced risk management.</div>
          </div>
          <button onClick={() => setActiveTab('plans')} className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold whitespace-nowrap" data-testid="upgrade-fno-cta">
            Upgrade Now →
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="strategies-grid">
        {strategies.map(strat => {
          const locked = strat.fno_only && !fnoAccess;
          return (
            <div key={strat.id} className={`bg-white/5 backdrop-blur border rounded-2xl p-5 transition group relative overflow-hidden ${locked ? 'border-pink-500/30 opacity-90' : 'border-white/10 hover:border-amber-500/40'}`}>
              {strat.fno_only && (
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                  <Crown className="w-3 h-3" /> F&O PRO
                </div>
              )}
              <div className="flex items-start justify-between mb-3 mt-1">
                <div className="pr-16">
                  <div className="text-xs text-amber-400 font-semibold uppercase tracking-wider">{strat.category}</div>
                  <h3 className="text-xl font-bold mt-1">{strat.name}</h3>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  strat.risk === 'Low' ? 'bg-emerald-500/20 text-emerald-300' :
                  strat.risk === 'High' ? 'bg-red-500/20 text-red-300' :
                  'bg-amber-500/20 text-amber-300'
                }`}>{strat.risk}</span>
              </div>
              <p className="text-sm text-slate-400 mb-3">{strat.description}</p>
              <div className="text-xs text-slate-500 italic mb-4">✓ {strat.ideal_for}</div>
              <button
                onClick={() => handleQuickStart(strat)}
                disabled={creating === strat.id}
                data-testid={`quick-start-${strat.id}`}
                className={`w-full py-2.5 ${locked ? 'bg-slate-700 cursor-not-allowed' : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'} text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition flex items-center justify-center gap-2`}
              >
                {creating === strat.id ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Creating…</>
                ) : locked ? (
                  <><Lock className="w-4 h-4" /> Pro-Plan Required</>
                ) : strat.fno_only ? (
                  <><Crown className="w-4 h-4" /> Configure F&O Bot</>
                ) : (
                  <><Play className="w-4 h-4" /> Quick-Start Paper</>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {fnoConfig && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-slate-900 border-2 border-pink-500/50 rounded-2xl p-6 max-w-md w-full" data-testid="fno-config-modal">
            <h3 className="text-xl font-bold mb-1 flex items-center gap-2"><Crown className="w-5 h-5 text-pink-400" /> Configure: {fnoConfig.strategy.name}</h3>
            <p className="text-xs text-slate-400 mb-4">F&O Risk Management Gates (mandatory)</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Instrument</label>
                <select value={fnoConfig.instrument} onChange={e => setFnoConfig({ ...fnoConfig, instrument: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="fno-instrument">
                  <option value="BANKNIFTY">BANKNIFTY (lot 15)</option>
                  <option value="NIFTY">NIFTY (lot 25)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Number of Lots</label>
                <input type="number" min="1" max="50" value={fnoConfig.fno_lots} onChange={e => setFnoConfig({ ...fnoConfig, fno_lots: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="fno-lots" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Daily Loss Limit (₹)</label>
                  <input type="number" value={fnoConfig.fno_daily_loss_limit} onChange={e => setFnoConfig({ ...fnoConfig, fno_daily_loss_limit: e.target.value })} className="w-full bg-slate-800 border border-red-500/30 rounded-lg px-3 py-2" data-testid="fno-loss-limit" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Daily Profit Target (₹)</label>
                  <input type="number" value={fnoConfig.fno_daily_profit_target} onChange={e => setFnoConfig({ ...fnoConfig, fno_daily_profit_target: e.target.value })} className="w-full bg-slate-800 border border-green-500/30 rounded-lg px-3 py-2" data-testid="fno-profit-target" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Max Trades/Day</label>
                <input type="number" min="1" max="50" value={fnoConfig.fno_max_trades_per_day} onChange={e => setFnoConfig({ ...fnoConfig, fno_max_trades_per_day: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="fno-max-trades" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={fnoConfig.fno_slippage_control} onChange={e => setFnoConfig({ ...fnoConfig, fno_slippage_control: e.target.checked })} className="w-4 h-4" data-testid="fno-slippage" />
                Slippage Control (SL-M / LIMIT only — recommended)
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setFnoConfig(null)} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg" data-testid="fno-cancel">Cancel</button>
                <button onClick={handleFnoConfirm} disabled={creating === fnoConfig.strategy.id} className="flex-1 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg font-bold disabled:opacity-50" data-testid="fno-deploy">
                  {creating === fnoConfig.strategy.id ? 'Deploying…' : 'Deploy F&O Bot'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============ My Bots ============
const BotsTab = ({ bots, strategies, stocks, reload, reloadPaper }) => {
  const [tradingBot, setTradingBot] = useState(null);

  const handleToggle = async (bot) => {
    try {
      await api.put(`/algotrading/bots/${bot.id}`, { is_active: !bot.is_active });
      toast.success(`Bot ${bot.is_active ? 'paused' : 'activated'}`);
      reload();
    } catch (e) {
      toast.error('Failed to update bot');
    }
  };
  const handleDelete = async (bot) => {
    if (!window.confirm(`Delete bot "${bot.strategy_name}"?`)) return;
    try {
      await api.delete(`/algotrading/bots/${bot.id}`);
      toast.success('Bot deleted');
      reload();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (bots.length === 0) {
    return (
      <div className="text-center py-16 bg-white/5 border border-white/10 rounded-2xl">
        <Cpu className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold mb-2">No bots yet</h3>
        <p className="text-slate-400 text-sm">Head to the Strategies tab and quick-start your first bot.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3" data-testid="bots-list">
        {bots.map(bot => (
          <div key={bot.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`bot-row-${bot.id}`}>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h4 className="text-lg font-bold">{bot.strategy_name}</h4>
                <span className={`text-xs px-2 py-0.5 rounded-full ${bot.is_active ? 'bg-green-500/20 text-green-300' : 'bg-slate-500/20 text-slate-400'}`}>
                  {bot.is_active ? '● ACTIVE' : '○ PAUSED'}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">{bot.mode.toUpperCase()}</span>
              </div>
              <div className="text-xs text-slate-400">
                Asset: {bot.asset_class} · Capital: ₹{bot.capital_allocated.toLocaleString('en-IN')} · Trades: {bot.total_trades} · P&L: ₹{bot.realized_pnl.toFixed(2)}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setTradingBot(bot)} className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg text-sm font-medium flex items-center gap-1" data-testid={`bot-trade-${bot.id}`}>
                <BarChart3 className="w-4 h-4" /> Trade
              </button>
              <button onClick={() => handleToggle(bot)} className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-1" data-testid={`bot-toggle-${bot.id}`}>
                {bot.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {bot.is_active ? 'Pause' : 'Resume'}
              </button>
              <button onClick={() => handleDelete(bot)} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm" data-testid={`bot-delete-${bot.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {tradingBot && (
        <ManualTradeModal bot={tradingBot} stocks={stocks} onClose={() => { setTradingBot(null); reload(); reloadPaper(); }} />
      )}
    </>
  );
};

const ManualTradeModal = ({ bot, stocks, onClose }) => {
  const [symbol, setSymbol] = useState(stocks[0]?.symbol || '');
  const [side, setSide] = useState('buy');
  const [qty, setQty] = useState(10);
  const [submitting, setSubmitting] = useState(false);

  const selected = stocks.find(s => s.symbol === symbol);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/algotrading/paper/trade', { bot_id: bot.id, symbol, side, qty: parseInt(qty, 10) });
      toast.success(`${side.toUpperCase()} ${qty} ${symbol} @ ₹${selected?.current_price}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trade failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-amber-500/30 rounded-2xl p-6 max-w-md w-full" data-testid="trade-modal">
        <h3 className="text-xl font-bold mb-1">Manual Paper Trade</h3>
        <p className="text-xs text-slate-400 mb-4">Bot: {bot.strategy_name}</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Symbol</label>
            <select value={symbol} onChange={e => setSymbol(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="trade-symbol">
              {stocks.map(s => (
                <option key={s.symbol} value={s.symbol}>{s.symbol} — ₹{s.current_price?.toFixed(2)}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Side</label>
              <select value={side} onChange={e => setSide(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="trade-side">
                <option value="buy">BUY</option>
                <option value="sell">SELL</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Quantity</label>
              <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="trade-qty" />
            </div>
          </div>
          {selected && (
            <div className="bg-slate-800/50 rounded-lg p-3 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">Current Price</span><span>₹{selected.current_price?.toFixed(2)}</span></div>
              <div className="flex justify-between font-bold mt-1"><span>Total</span><span className="text-amber-300">₹{(selected.current_price * qty).toFixed(2)}</span></div>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg" data-testid="trade-cancel">Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold disabled:opacity-50" data-testid="trade-submit">
              {submitting ? 'Executing…' : `Execute ${side.toUpperCase()}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Paper Trading ============
// Tiny haptic helper — safe-noop on unsupported browsers
const haptic = (pattern = 15) => {
  try { if (navigator.vibrate) navigator.vibrate(pattern); } catch (_) { /* ignore */ }
};

// Pull-to-refresh hook — mobile only, triggers `onRefresh` when user drags down ≥70px from scrollTop=0
const usePullToRefresh = (onRefresh, enabled = true) => {
  const startY = useRef(0);
  const pullingRef = useRef(false);
  const armedHapticRef = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const handleTouchStart = (e) => {
      if (window.scrollY > 4) return;
      startY.current = e.touches[0].clientY;
      pullingRef.current = true;
      armedHapticRef.current = false;
    };
    const handleTouchMove = (e) => {
      if (!pullingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && window.scrollY <= 4) {
        const damped = Math.min(120, dy * 0.55);
        setPull(damped);
        // Haptic tick once when crossing the release threshold
        if (damped >= 70 && !armedHapticRef.current) {
          armedHapticRef.current = true;
          haptic(15);
        } else if (damped < 70 && armedHapticRef.current) {
          armedHapticRef.current = false;
        }
      } else if (dy < 0) {
        pullingRef.current = false;
        setPull(0);
      }
    };
    const handleTouchEnd = async () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      const reached = pull >= 70;
      if (reached) {
        haptic([20, 30, 20]); // confirm pattern on release
        setRefreshing(true);
        try { await onRefresh(); } finally {
          setTimeout(() => { setRefreshing(false); setPull(0); }, 400);
        }
      } else {
        setPull(0);
      }
    };
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, pull, onRefresh]);

  return { pull, refreshing };
};

const PullToRefreshIndicator = ({ pull, refreshing }) => {
  if (pull <= 0 && !refreshing) return null;
  const progress = Math.min(100, (pull / 70) * 100);
  const ready = progress >= 100;
  return (
    <div
      className="fixed top-0 left-0 right-0 z-40 flex items-center justify-center pointer-events-none sm:hidden"
      style={{ transform: `translateY(${refreshing ? 20 : Math.max(0, pull - 20)}px)`, transition: refreshing ? 'transform 0.2s' : 'none' }}
    >
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${ready || refreshing ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200' : 'bg-amber-500/20 border-amber-400/40 text-amber-200'}`}>
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} style={{ transform: refreshing ? '' : `rotate(${progress * 1.8}deg)` }} />
        {refreshing ? 'Refreshing…' : ready ? 'Release to refresh' : 'Pull to refresh'}
      </div>
    </div>
  );
};

const PaperTab = ({ paperWallet, paperTrades, stocks, bots, reload }) => {
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(null); // {type:'all'} or {type:'one', symbol}
  const [autoCloseModal, setAutoCloseModal] = useState(null); // position object
  const [quickTrade, setQuickTrade] = useState(false);
  const { pull, refreshing } = usePullToRefresh(reload, true);

  const handleReset = async () => {
    if (!window.confirm('Reset paper wallet to ₹1 Crore and clear all positions?')) return;
    try {
      await api.post('/algotrading/paper/reset');
      toast.success('Paper wallet reset to ₹1 Crore');
      reload();
    } catch (e) { toast.error('Reset failed'); }
  };

  const executeCloseAll = async () => {
    setConfirmClose(null);
    setClosing(true);
    try {
      const { data } = await api.post('/algotrading/paper/positions/close-all');
      const pnl = data.total_realized_pnl || 0;
      if (data.count_closed > 0) {
        const msg = `Closed ${data.count_closed} positions · Net P&L ₹${pnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
        if (pnl >= 0) { haptic([30, 60, 30]); toast.success(msg, { style: { background: '#065f46', color: '#ecfdf5' } }); }
        else { haptic([60, 40, 60, 40, 60]); toast.error(msg, { style: { background: '#7f1d1d', color: '#fef2f2' } }); }
      }
      if (data.count_failed > 0) {
        haptic([80, 40, 80]);
        toast.error(`${data.count_failed} positions failed to close`, { style: { background: '#7f1d1d', color: '#fef2f2' } });
      }
      reload();
    } catch (e) {
      haptic([80, 40, 80]);
      toast.error(e.response?.data?.detail || 'Close All failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally {
      setClosing(false);
    }
  };

  const executeCloseOne = async (symbol) => {
    setConfirmClose(null);
    setClosing(true);
    try {
      const { data } = await api.post(`/algotrading/paper/positions/${symbol}/close`);
      const pnl = data.realized_pnl || 0;
      const msg = `${symbol} closed @ ₹${data.exit_price?.toFixed(2)} · P&L ₹${pnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      if (pnl >= 0) { haptic([25, 50, 25]); toast.success(msg, { style: { background: '#065f46', color: '#ecfdf5' } }); }
      else { haptic([50, 30, 50]); toast.error(msg, { style: { background: '#7f1d1d', color: '#fef2f2' } }); }
      reload();
    } catch (e) {
      haptic([80, 40, 80]);
      toast.error(e.response?.data?.detail || `Failed to close ${symbol}`, { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="space-y-6">
      <PullToRefreshIndicator pull={pull} refreshing={refreshing} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        <StatCard label="Cash Available" value={`₹${(paperWallet?.wallet?.cash || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`} testid="paper-stat-cash" />
        <StatCard label="Market Value" value={`₹${(paperWallet?.market_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`} testid="paper-stat-mv" />
        <StatCard label="Realized P&L" value={`₹${(paperWallet?.wallet?.realized_pnl || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
          color={paperWallet?.wallet?.realized_pnl >= 0 ? 'text-emerald-300' : 'text-red-300'} testid="paper-stat-realized" />
        <StatCard label="Unrealized P&L" value={`₹${(paperWallet?.unrealized_pnl || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
          color={paperWallet?.unrealized_pnl >= 0 ? 'text-emerald-300' : 'text-red-300'} testid="paper-stat-unrealized" />
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {paperWallet?.positions?.length > 0 && (
          <button
            onClick={() => setConfirmClose({ type: 'all' })}
            disabled={closing}
            className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="paper-close-all-btn"
          >
            <Pause className="w-4 h-4" /> Close All ({paperWallet.positions.length})
          </button>
        )}
        <button onClick={handleReset} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm flex items-center gap-2" data-testid="paper-reset-btn">
          <RefreshCw className="w-4 h-4" /> Reset to ₹1 Cr
        </button>
      </div>

      <div>
        <h3 className="text-lg font-bold mb-3">Open Positions</h3>
        {!paperWallet?.positions?.length ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center text-slate-400">No open positions. Go to Bots → Trade to start.</div>
        ) : (
          <>
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-white/5 text-xs uppercase text-slate-400">
                <tr>
                  <th className="p-3 text-left">Symbol</th>
                  <th className="p-3 text-left">Bot</th>
                  <th className="p-3 text-right">Qty</th>
                  <th className="p-3 text-right">Avg</th>
                  <th className="p-3 text-right">CMP</th>
                  <th className="p-3 text-right">P&L</th>
                  <th className="p-3 text-center">Auto-Close</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paperWallet.positions.map((p, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid={`paper-position-${p.symbol}`}>
                    <td className="p-3 font-bold">{p.symbol}</td>
                    <td className="p-3 text-xs text-slate-300">
                      {p.bot_name || 'Manual'}
                      {p.bot_id && <div className="text-[10px] text-slate-500 truncate max-w-[120px]" title={p.bot_id}>id: {p.bot_id.slice(0, 6)}…</div>}
                    </td>
                    <td className="p-3 text-right">{p.qty}</td>
                    <td className="p-3 text-right">₹{p.avg_price?.toFixed(2)}</td>
                    <td className="p-3 text-right">₹{p.current_price?.toFixed(2)}</td>
                    <td className={`p-3 text-right font-bold ${p.unrealized_pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      ₹{p.unrealized_pnl?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-3 text-center">
                      {p.auto_close_enabled ? (
                        <div className="inline-flex flex-col items-center gap-0.5 text-[11px]">
                          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-semibold">AUTO ON</span>
                          <span className="text-slate-400">
                            {p.target_value ? `T:₹${p.target_value}` : (p.target_pnl ? `T:₹${p.target_pnl}` : '')}
                            {(p.target_value || p.target_pnl) && (p.stop_loss_value || p.stop_loss_pnl) ? ' · ' : ''}
                            {p.stop_loss_value ? `SL:₹${p.stop_loss_value}` : (p.stop_loss_pnl ? `SL:₹${p.stop_loss_pnl}` : '')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setAutoCloseModal(p)}
                          className="px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 rounded"
                          data-testid={`paper-set-auto-${p.symbol}`}
                        >
                          Target/SL
                        </button>
                        <button
                          onClick={() => setConfirmClose({ type: 'one', symbol: p.symbol })}
                          disabled={closing}
                          className="px-2 py-1 text-xs bg-red-500/30 hover:bg-red-500/40 text-red-200 rounded disabled:opacity-50"
                          data-testid={`paper-close-${p.symbol}`}
                        >
                          Close
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="text-[10px] text-amber-300/70 text-center mt-1 sm:hidden">← swipe table to see Bot · Auto-Close · Actions →</div>
          </>
        )}
      </div>

      <div>
        <h3 className="text-lg font-bold mb-3">Recent Trades</h3>
        {!paperTrades.length ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center text-slate-400">No trades yet.</div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-white/5 text-xs uppercase text-slate-400">
                <tr><th className="p-3 text-left">Time</th><th className="p-3 text-left">Symbol</th><th className="p-3">Side</th><th className="p-3 text-right">Qty</th><th className="p-3 text-right">Price</th><th className="p-3 text-right">P&L</th></tr>
              </thead>
              <tbody>
                {paperTrades.map(t => (
                  <tr key={t.id} className="border-t border-white/5">
                    <td className="p-3 text-xs text-slate-400">{new Date(t.executed_at).toLocaleString('en-IN', { hour12: false })}</td>
                    <td className="p-3 font-bold">{t.symbol}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${t.side === 'buy' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                        {t.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-right">{t.qty}</td>
                    <td className="p-3 text-right">₹{t.price?.toFixed(2)}</td>
                    <td className={`p-3 text-right ${t.realized_pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {t.realized_pnl === 0 ? '—' : `₹${t.realized_pnl.toFixed(2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {paperTrades.length > 0 && <div className="text-[10px] text-amber-300/70 text-center mt-1 sm:hidden">← swipe table to see Qty · Price · P&L →</div>}
      </div>

      {confirmClose && (
        <CloseConfirmModal
          mode={confirmClose}
          positions={paperWallet?.positions || []}
          onCancel={() => setConfirmClose(null)}
          onConfirm={() => confirmClose.type === 'all' ? executeCloseAll() : executeCloseOne(confirmClose.symbol)}
        />
      )}

      {autoCloseModal && (
        <AutoCloseModal
          position={autoCloseModal}
          onClose={() => setAutoCloseModal(null)}
          onSaved={() => { setAutoCloseModal(null); reload(); }}
        />
      )}

      {/* Quick Trade FAB — mobile-first, always visible */}
      <button
        onClick={() => { haptic(10); setQuickTrade(true); }}
        className="fixed bottom-6 right-4 sm:right-6 z-40 group flex items-center gap-2 px-4 py-3.5 sm:py-4 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold shadow-2xl shadow-emerald-500/40 active:scale-95 transition"
        data-testid="quick-trade-fab"
        title="Quick Buy"
      >
        <Zap className="w-5 h-5" />
        <span className="hidden sm:inline">Quick Trade</span>
        <span className="sm:hidden text-xs">BUY</span>
      </button>

      {quickTrade && (
        <QuickTradeSheet
          stocks={stocks}
          bots={bots}
          paperWallet={paperWallet}
          onClose={() => setQuickTrade(false)}
          onDone={() => { setQuickTrade(false); reload(); }}
        />
      )}
    </div>
  );
};

// =================== Quick Trade Sheet — single-tap BUY/SELL for mobile ===================
const LAST_TRADE_KEY = 'algotrading:last_trade';

const QuickTradeSheet = ({ stocks, bots, paperWallet, onClose, onDone }) => {
  const activeBots = (bots || []).filter(b => b.is_active && b.mode === 'paper');
  const [botId, setBotId] = useState(activeBots[0]?.id || '');
  const [symbol, setSymbol] = useState(stocks[0]?.symbol || 'RELIANCE');
  const [side, setSide] = useState('buy');
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [repeating, setRepeating] = useState(null); // index of trade being repeated, or null
  const [recentTrades, setRecentTrades] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // Hydrate from localStorage + sync from server (server takes precedence; merge dedup)
  useEffect(() => {
    let cancelled = false;
    // Local fallback first
    let localList = [];
    try {
      const raw = localStorage.getItem(LAST_TRADE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.symbol) localList = [parsed];
      }
    } catch (_) { /* ignore */ }
    if (localList.length) setRecentTrades(localList);

    // Server sync (authoritative)
    (async () => {
      try {
        const { data } = await api.get('/algotrading/paper/recent-manual-trades?limit=3');
        if (cancelled) return;
        const server = (data?.trades || []).map(t => ({
          bot_id: t.bot_id, symbol: t.symbol, side: t.side, qty: t.qty,
          bot_name: t.bot_name, bot_active: t.bot_active, ts: t.last_executed_at,
          trend: t.trend, historical_pnl: t.historical_pnl,
          historical_wins: t.historical_wins, historical_losses: t.historical_losses,
          historical_trades: t.historical_trades,
        }));
        // Merge unique with local items not present in server (e.g., very recent unsynced)
        const keyOf = x => `${x.bot_id}|${x.symbol}|${x.side}|${x.qty}`;
        const seen = new Set(server.map(keyOf));
        const merged = [...server];
        for (const l of localList) {
          if (!seen.has(keyOf(l))) merged.push(l);
        }
        setRecentTrades(merged.slice(0, 3));
      } catch (_) { /* keep local fallback */ }
      finally { if (!cancelled) setLoadingRecent(false); }
    })();

    return () => { cancelled = true; };
  }, []);

  const selected = stocks.find(s => s.symbol === symbol);
  const cash = paperWallet?.wallet?.cash || 0;
  const cost = selected ? selected.current_price * qty : 0;
  const canBuy = side === 'sell' || cost <= cash;
  const noBots = activeBots.length === 0;

  const persistLast = (payload) => {
    try { localStorage.setItem(LAST_TRADE_KEY, JSON.stringify(payload)); } catch (_) { /* ignore */ }
  };

  const placeTrade = async (payload) => {
    const { data } = await api.post('/algotrading/paper/trade', payload);
    const px = data?.trade?.price?.toFixed(2) || selected?.current_price?.toFixed(2) || '—';
    haptic([20, 40, 20]);
    toast.success(`${payload.side.toUpperCase()} ${payload.qty} ${payload.symbol} @ ₹${px}`, {
      style: { background: '#065f46', color: '#ecfdf5' },
    });
    persistLast({ ...payload, ts: Date.now() });
  };

  const submit = async () => {
    if (!botId || !symbol || qty < 1) return;
    setSubmitting(true);
    try {
      await placeTrade({ bot_id: botId, symbol, side, qty: parseInt(qty, 10) });
      onDone();
    } catch (e) {
      haptic([80, 40, 80]);
      toast.error(e.response?.data?.detail || 'Trade failed', {
        style: { background: '#7f1d1d', color: '#fef2f2' },
      });
    } finally { setSubmitting(false); }
  };

  // One-tap repeat — fires the chosen recent trade immediately. Falls back to first active bot if old bot is gone.
  const repeatTrade = async (trade, idx) => {
    const stillActive = activeBots.find(b => b.id === trade.bot_id);
    const useBot = stillActive ? trade.bot_id : activeBots[0]?.id;
    if (!useBot) {
      haptic([80, 40, 80]);
      toast.error('No active bot available to repeat', { style: { background: '#7f1d1d', color: '#fef2f2' } });
      return;
    }
    haptic(12);
    setRepeating(idx);
    try {
      await placeTrade({
        bot_id: useBot, symbol: trade.symbol, side: trade.side, qty: parseInt(trade.qty, 10),
      });
      onDone();
    } catch (e) {
      haptic([80, 40, 80]);
      toast.error(e.response?.data?.detail || 'Repeat failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally { setRepeating(null); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="quick-trade-sheet"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-slate-900 border-t border-white/10 sm:border sm:rounded-2xl rounded-t-3xl p-5 w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Drag handle (mobile bottom-sheet feel) */}
        <div className="mx-auto w-10 h-1 bg-white/20 rounded-full mb-4 sm:hidden" />
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-400" /> Quick Trade
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="quick-trade-close">✕</button>
        </div>

        {noBots ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200" data-testid="quick-trade-nobots">
            ⚠️ Koi active paper-mode bot nahi hai. Pehle <b>My Bots → Create Bot</b> se ek bot banao.
          </div>
        ) : (
          <>
            {/* Top 3 Recent — one-tap repeat (synced from server + localStorage) */}
            {(recentTrades.length > 0 || loadingRecent) && (
              <div className="mb-4" data-testid="quick-trade-recent">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> One-Tap Repeat · Top {Math.min(3, recentTrades.length) || 'Recent'}
                </div>
                {loadingRecent && recentTrades.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">Loading recent trades…</div>
                ) : (
                  <div className="space-y-1.5">
                    {recentTrades.map((t, i) => {
                      const winRate = t.historical_trades ? Math.round((t.historical_wins / t.historical_trades) * 100) : null;
                      const trendIcon = t.trend === 'profit' ? '▲' : t.trend === 'loss' ? '▼' : '◆';
                      const trendColor = t.trend === 'profit' ? 'text-emerald-300' : t.trend === 'loss' ? 'text-red-300' : 'text-slate-400';
                      const pnlText = (t.historical_pnl ?? 0) !== 0
                        ? `${t.historical_pnl > 0 ? '+' : ''}₹${Math.round(t.historical_pnl).toLocaleString('en-IN')}`
                        : 'new';
                      return (
                        <button
                          key={`${t.bot_id}-${t.symbol}-${t.side}-${t.qty}-${i}`}
                          onClick={() => repeatTrade(t, i)}
                          disabled={repeating !== null || submitting}
                          className={`w-full px-3 py-2 rounded-xl border border-dashed flex items-center justify-between gap-2 transition active:scale-95 disabled:opacity-50 ${
                            t.side === 'buy'
                              ? 'border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200'
                              : 'border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200'
                          }`}
                          data-testid={`quick-trade-repeat-${i}`}
                        >
                          <span className="flex items-center gap-2 text-sm font-semibold min-w-0">
                            <RefreshCw className={`w-4 h-4 flex-shrink-0 ${repeating === i ? 'animate-spin' : ''}`} />
                            <span className="truncate">{repeating === i ? 'Repeating…' : `${t.side.toUpperCase()} ${t.qty} ${t.symbol}`}</span>
                          </span>
                          <span className="flex items-center gap-1.5 flex-shrink-0">
                            <span className={`text-[10px] font-mono ${trendColor}`} title={`Net P&L on ${t.symbol}: ${pnlText}${winRate !== null ? ` · win-rate ${winRate}%` : ''}`}>
                              {trendIcon} {pnlText}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Side toggle (BUY / SELL) */}
            <div className="grid grid-cols-2 gap-2 mb-4 bg-white/5 rounded-xl p-1">
              <button
                onClick={() => { haptic(8); setSide('buy'); }}
                className={`py-2.5 rounded-lg font-bold text-sm transition ${side === 'buy' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30' : 'text-slate-400'}`}
                data-testid="quick-trade-buy"
              >BUY</button>
              <button
                onClick={() => { haptic(8); setSide('sell'); }}
                className={`py-2.5 rounded-lg font-bold text-sm transition ${side === 'sell' ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' : 'text-slate-400'}`}
                data-testid="quick-trade-sell"
              >SELL</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Bot ({activeBots.length} active)</label>
                <select value={botId} onChange={e => setBotId(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="quick-trade-bot">
                  {activeBots.map(b => <option key={b.id} value={b.id}>{b.strategy_name} · ₹{Number(b.capital_allocated || 0).toLocaleString('en-IN')}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Symbol</label>
                <select value={symbol} onChange={e => setSymbol(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm" data-testid="quick-trade-symbol">
                  {stocks.map(s => <option key={s.symbol} value={s.symbol}>{s.symbol} — ₹{s.current_price?.toFixed(2)}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Quantity</label>
                <div className="flex items-stretch gap-2">
                  <button onClick={() => { haptic(8); setQty(q => Math.max(1, Number(q) - 1)); }} className="w-12 bg-white/5 hover:bg-white/10 rounded-lg text-xl font-bold" data-testid="quick-trade-qty-minus">−</button>
                  <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 text-center text-lg font-bold" data-testid="quick-trade-qty" />
                  <button onClick={() => { haptic(8); setQty(q => Number(q) + 1); }} className="w-12 bg-white/5 hover:bg-white/10 rounded-lg text-xl font-bold" data-testid="quick-trade-qty-plus">+</button>
                </div>
                <div className="flex gap-1 mt-2">
                  {[10, 50, 100, 500].map(n => (
                    <button key={n} onClick={() => { haptic(6); setQty(n); }} className="flex-1 py-1 text-[11px] bg-white/5 hover:bg-white/10 rounded text-slate-300" data-testid={`quick-trade-qty-preset-${n}`}>{n}</button>
                  ))}
                </div>
              </div>

              {selected && (
                <div className="bg-slate-800/50 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-slate-400">Price</span><span>₹{selected.current_price?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Quantity</span><span>{qty}</span></div>
                  <div className="flex justify-between font-bold pt-1 border-t border-white/5">
                    <span>Total {side === 'buy' ? 'Cost' : 'Proceeds'}</span>
                    <span className="text-amber-300">₹{cost.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  {side === 'buy' && (
                    <div className={`flex justify-between text-[11px] ${canBuy ? 'text-slate-500' : 'text-red-400'}`}>
                      <span>Cash available</span>
                      <span>₹{cash.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={submit}
                disabled={submitting || !canBuy || repeating !== null}
                className={`w-full py-3.5 rounded-xl font-extrabold text-base transition active:scale-95 disabled:opacity-50 disabled:active:scale-100 ${
                  side === 'buy'
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/40'
                    : 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg shadow-red-500/40'
                }`}
                data-testid="quick-trade-submit"
              >
                {submitting ? 'Placing…' : `${side.toUpperCase()} ${qty} ${symbol}`}
              </button>
              {!canBuy && side === 'buy' && (
                <p className="text-xs text-red-300 text-center">Insufficient paper cash for this trade</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CloseConfirmModal = ({ mode, positions, onCancel, onConfirm }) => {
  const isAll = mode.type === 'all';
  const target = isAll ? positions : positions.filter(p => p.symbol === mode.symbol);
  const totalPnl = target.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="close-confirm-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-2 text-amber-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {isAll ? `Close All ${target.length} Positions?` : `Close ${mode.symbol}?`}
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          This will market-sell {isAll ? 'every open position' : `your ${mode.symbol} holding`} at the current price. This action cannot be undone.
        </p>
        <div className="bg-white/5 rounded-lg p-3 mb-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Positions</span><span className="font-bold">{target.length}</span></div>
          <div className="flex justify-between mt-1">
            <span className="text-slate-400">Estimated P&L</span>
            <span className={`font-bold ${totalPnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
              ₹{totalPnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg text-slate-200" data-testid="close-cancel-btn">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 rounded-lg font-bold text-white" data-testid="close-confirm-btn">
            Yes, Close {isAll ? 'All' : mode.symbol}
          </button>
        </div>
      </div>
    </div>
  );
};

const AutoCloseModal = ({ position, onClose, onSaved }) => {
  const [enabled, setEnabled] = useState(position.auto_close_enabled || false);
  const [targetValue, setTargetValue] = useState(position.target_value ?? '');
  const [stopLossValue, setStopLossValue] = useState(position.stop_loss_value ?? '');
  const [targetPnl, setTargetPnl] = useState(position.target_pnl ?? '');
  const [stopLossPnl, setStopLossPnl] = useState(position.stop_loss_pnl ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/algotrading/paper/positions/${position.symbol}/auto-close`, {
        enabled,
        target_value: targetValue === '' ? null : Number(targetValue),
        stop_loss_value: stopLossValue === '' ? null : Number(stopLossValue),
        target_pnl: targetPnl === '' ? null : Number(targetPnl),
        stop_loss_pnl: stopLossPnl === '' ? null : Number(stopLossPnl),
      });
      toast.success(enabled ? `Auto-close enabled for ${position.symbol}` : `Auto-close disabled for ${position.symbol}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="auto-close-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-1">Auto-Close · {position.symbol}</h3>
        <p className="text-slate-400 text-xs mb-4">
          Avg ₹{position.avg_price?.toFixed(2)} · CMP ₹{position.current_price?.toFixed(2)} · Qty {position.qty}
        </p>

        <div className="flex items-center justify-between bg-white/5 rounded-lg p-3 mb-4">
          <span className="text-sm">Enable Auto-Close</span>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`w-12 h-6 rounded-full transition relative ${enabled ? 'bg-emerald-500' : 'bg-slate-600'}`}
            data-testid="auto-close-toggle"
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition ${enabled ? 'left-6' : 'left-0.5'}`} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-slate-400">Target Price (₹)</label>
            <input type="number" step="0.01" value={targetValue} onChange={e => setTargetValue(e.target.value)}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
              placeholder="e.g. 3100" data-testid="auto-close-target-price" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Stop-Loss Price (₹)</label>
            <input type="number" step="0.01" value={stopLossValue} onChange={e => setStopLossValue(e.target.value)}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
              placeholder="e.g. 2800" data-testid="auto-close-sl-price" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Target P&L (₹)</label>
            <input type="number" step="0.01" value={targetPnl} onChange={e => setTargetPnl(e.target.value)}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
              placeholder="e.g. 5000" data-testid="auto-close-target-pnl" />
          </div>
          <div>
            <label className="text-xs text-slate-400">SL P&L (₹, negative)</label>
            <input type="number" step="0.01" value={stopLossPnl} onChange={e => setStopLossPnl(e.target.value)}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
              placeholder="e.g. -2000" data-testid="auto-close-sl-pnl" />
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mb-4">
          Engine checks every 15s. Triggers on whichever condition hits first.
        </p>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg text-slate-200" data-testid="auto-close-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-bold text-white disabled:opacity-50" data-testid="auto-close-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color = 'text-white', testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3 md:p-4" data-testid={testid}>
    <div className="text-[10px] md:text-xs text-slate-400 mb-1">{label}</div>
    <div className={`text-base md:text-xl font-bold break-all ${color}`}>{value}</div>
  </div>
);

// ============ API Vault ============
const ApiVaultTab = ({ apiKeys, brokers, reload }) => {
  const [wizardBroker, setWizardBroker] = useState(null); // string broker id when wizard open
  const [showBrokerPicker, setShowBrokerPicker] = useState(false);

  const handleValidate = async (id) => {
    try {
      const r = await api.post(`/algotrading/api-keys/${id}/validate`);
      if (r.data.success) toast.success(r.data.message || 'Validated ✓');
      else if (r.data.status === 'geo_blocked') toast.warning(r.data.message, { duration: 10000 });
      else toast.error(r.data.message || 'Validation failed');
      reload();
    } catch (e) { toast.error('Validation failed'); }
  };
  const handleMarkReady = async (id) => {
    if (!window.confirm('Mark these keys as ready?\n\nUse this only if you have verified the keys work on your own VPN / device. The platform cannot validate from this server due to broker geo-restrictions.')) return;
    try {
      await api.post(`/algotrading/api-keys/${id}/mark-ready`);
      toast.success('Marked ready ✓');
      reload();
    } catch (e) { toast.error('Could not mark ready'); }
  };
  const handleKiteLogin = async (id) => {
    try {
      const r = await api.get('/algotrading/live/kite/login-url');
      window.open(r.data.login_url, '_blank', 'noopener,noreferrer,width=900,height=700');
      setTimeout(() => {
        const token = window.prompt(
          'After signing in on Zerodha, copy the "request_token" from the URL and paste it here:'
        );
        if (token && token.trim()) {
          api.post('/algotrading/live/kite/exchange-token', { request_token: token.trim() })
            .then(res => { toast.success(`✓ Connected as ${res.data.user_name || res.data.user_id}`); reload(); })
            .catch(err => toast.error(err.response?.data?.detail || 'Kite token exchange failed'));
        }
      }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start Kite login');
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this API key?')) return;
    try { await api.delete(`/algotrading/api-keys/${id}`); toast.success('Deleted'); reload(); } catch (e) { toast.error('Failed'); }
  };

  const enabledBrokers = (brokers || []).filter(b => b.is_enabled);

  return (
    <div className="space-y-4">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200/90">
        🔒 All API keys are encrypted at rest (Fernet). We never expose your secret in any response.
        <br />🧪 <b>Binance Testnet</b> validation is LIVE — use the testnet.binance.vision keys to safely test the integration.
        <br />🪄 <b>Setup Wizard</b> walks you through key generation for each broker — click below to start.
      </div>

      {/* Broker picker / wizard launcher */}
      {!showBrokerPicker ? (
        <button onClick={() => setShowBrokerPicker(true)}
          className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold flex items-center gap-2" data-testid="add-api-key-btn">
          <Key className="w-4 h-4" /> Add Broker API Key
        </button>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="broker-picker">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">Choose your broker to start the Setup Wizard</h3>
            <button onClick={() => setShowBrokerPicker(false)} className="text-slate-400 hover:text-white text-sm" data-testid="picker-cancel">Cancel</button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {enabledBrokers.map(b => (
              <button key={b.id} onClick={() => { setShowBrokerPicker(false); setWizardBroker(b.id); }}
                className="text-left bg-slate-800/60 hover:bg-slate-800 border border-white/10 hover:border-amber-500/50 rounded-xl p-3 transition group"
                data-testid={`pick-broker-${b.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-white">{b.name}</span>
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition" />
                </div>
                <div className="flex gap-1.5 flex-wrap mb-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 uppercase tracking-wider">{b.category}</span>
                  {b.country && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-500/20 text-slate-300">{b.country}</span>}
                </div>
                {b.notes && <p className="text-xs text-slate-400 line-clamp-2">{b.notes}</p>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Wizard modal */}
      {wizardBroker && (
        <BrokerSetupWizard
          broker={wizardBroker}
          brokers={brokers}
          onClose={() => setWizardBroker(null)}
          onSaved={() => { setWizardBroker(null); reload(); }}
        />
      )}

      {apiKeys.length === 0 ? (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl text-slate-400">No API keys saved yet.</div>
      ) : (
        <div className="space-y-3" data-testid="api-keys-list">
          {apiKeys.map(k => (
            <div key={k.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold">{k.label}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">{k.broker?.toUpperCase()}</span>
                  {k.is_testnet && <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300">TESTNET</span>}
                  {k.validation_status === 'connected' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Connected</span>
                  ) : k.validation_status === 'user_attested' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center gap-1" title="User-attested ready"><ShieldCheck className="w-3 h-3" /> User-Ready</span>
                  ) : k.validation_status === 'geo_blocked' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300" title="Broker blocks this server's region — use VPN or alt broker">⚠️ Geo-Blocked</span>
                  ) : k.validation_status === 'failed' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">Failed</span>
                  ) : k.validation_status === 'saved' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Saved (Phase 2)</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400">Pending</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 font-mono">API: {k.api_key_masked} · Secret: {k.api_secret_masked}</div>
                {k.validation_message && <div className="text-xs text-slate-500 mt-1">{k.validation_message}</div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                {k.broker === 'zerodha' && (
                  <button onClick={() => handleKiteLogin(k.id)} className="px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 rounded-lg text-sm flex items-center gap-1" data-testid={`kite-connect-${k.id}`}>
                    <ExternalLink className="w-4 h-4" /> Connect Kite
                  </button>
                )}
                <button onClick={() => handleValidate(k.id)} className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg text-sm" data-testid={`validate-${k.id}`}>Validate</button>
                {(k.validation_status === 'geo_blocked' || k.validation_status === 'failed') && (
                  <button onClick={() => handleMarkReady(k.id)} className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-sm" data-testid={`mark-ready-${k.id}`} title="I've verified these keys work on my end">✓ Mark Ready</button>
                )}
                <button onClick={() => handleDelete(k.id)} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm" data-testid={`delete-key-${k.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Plans ============
const PlansTab = ({ tiers, mySubscription, walletBalance, trialStatus, reload }) => {
  const [purchasing, setPurchasing] = useState(null);
  const [confirm, setConfirm] = useState(null); // {tier, mode}
  const [timeLeft, setTimeLeft] = useState('');
  const [claimingTrial, setClaimingTrial] = useState(false);

  const claimFreeTrial = async () => {
    if (!window.confirm('Activate your 1-Day Free Trial?\n\n✅ 2 paper trading bots\n✅ ₹2,00,000 virtual capital cap\n✅ Valid for 24 hours\n\nThis trial can be claimed only ONCE per account.')) return;
    setClaimingTrial(true);
    try {
      const r = await api.post('/algotrading/subscriptions/start-trial');
      toast.success(r.data.message || '🎉 1-day free trial activated!', { style: { background: '#065f46', color: '#ecfdf5' } });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Trial activation failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally { setClaimingTrial(false); }
  };

  // Live countdown timer for active subscription
  useEffect(() => {
    if (!mySubscription?.expires_at) { setTimeLeft(''); return; }
    const update = () => {
      const exp = new Date(mySubscription.expires_at);
      const now = new Date();
      const diff = exp - now;
      if (diff <= 0) { setTimeLeft('Expired'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [mySubscription?.expires_at]);

  const requestPurchase = (tier, mode) => {
    if (mySubscription) {
      toast.error(`You already have an active "${mySubscription.tier_name}" plan. Wait until expiry to buy a new one.`, { style: { background: '#7f1d1d', color: '#fef2f2' } });
      return;
    }
    setConfirm({ tier, mode });
  };

  const executePurchase = async () => {
    if (!confirm) return;
    const { tier, mode } = confirm;
    setConfirm(null);
    setPurchasing(`${tier.id}-${mode}`);
    try {
      const r = await api.post('/algotrading/subscriptions/purchase', {
        tier_id: tier.id,
        payment_mode: mode,
      });
      toast.success(`🎉 ${tier.name} activated! Ref: ${r.data.payment?.ref}`, { style: { background: '#065f46', color: '#ecfdf5' } });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally { setPurchasing(null); }
  };

  // Compute expiry percentage for progress bar
  const expiryPct = (() => {
    if (!mySubscription?.expires_at || !mySubscription?.started_at) return 0;
    const start = new Date(mySubscription.started_at).getTime();
    const end = new Date(mySubscription.expires_at).getTime();
    const now = Date.now();
    return Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100));
  })();

  return (
    <div className="space-y-4">
      {/* 1-Day Free Trial CTA — only when trial not used + no active subscription */}
      {trialStatus && !trialStatus.trial_used && !mySubscription && (
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-600/30 via-pink-500/20 to-amber-500/30 border-2 border-dashed border-amber-400/50 rounded-2xl p-5" data-testid="free-trial-cta">
          <div className="absolute -top-8 -right-8 text-9xl opacity-10 select-none pointer-events-none">🎁</div>
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 text-[10px] font-bold uppercase tracking-wider mb-2">
                <span className="animate-pulse">★</span> Limited · One-Time Offer
              </div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-white">🎁 Start 1-Day FREE Trial</h3>
              <p className="text-sm text-slate-200/90 mt-1">
                Unlock <b>2 paper trading bots</b> & <b>₹2,00,000 virtual capital</b> for 24 hours. No card. No catch. Available <b>once per account</b>.
              </p>
            </div>
            <button
              onClick={claimFreeTrial}
              disabled={claimingTrial}
              className="px-6 py-3 bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-white font-extrabold rounded-xl shadow-lg shadow-amber-500/30 active:scale-95 transition disabled:opacity-50 whitespace-nowrap"
              data-testid="claim-trial-btn"
            >
              {claimingTrial ? 'Activating…' : 'Claim Free Trial →'}
            </button>
          </div>
        </div>
      )}

      {trialStatus && trialStatus.trial_used && !mySubscription && (
        <div className="bg-slate-800/50 border border-white/10 rounded-xl p-3 text-xs text-slate-400 flex items-center gap-2" data-testid="trial-used-info">
          <span className="text-base">🎁</span>
          <span>Your 1-day free trial has been used. Choose a paid plan below to continue trading.</span>
        </div>
      )}

      {mySubscription && (
        <div className="bg-gradient-to-br from-emerald-500/15 to-teal-500/10 border border-emerald-500/30 rounded-2xl p-5" data-testid="active-sub-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-emerald-300 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> ACTIVE PLAN
              </div>
              <h3 className="text-2xl font-extrabold mt-1">{mySubscription.tier_name}</h3>
              <div className="text-sm text-slate-300 mt-1">
                Max Bots: {mySubscription.max_bots} · Max Capital: ₹{mySubscription.max_capital?.toLocaleString('en-IN')}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Time remaining</div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-300 tabular-nums" data-testid="sub-countdown">{timeLeft || '—'}</div>
              <div className="text-xs text-slate-400 mt-0.5">Expires {new Date(mySubscription.expires_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-1000"
                style={{ width: `${expiryPct}%` }}
                data-testid="sub-progress"
              />
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>Started {new Date(mySubscription.started_at || Date.now()).toLocaleDateString('en-IN')}</span>
              <span>{expiryPct.toFixed(0)}% used</span>
            </div>
          </div>
        </div>
      )}

      {mySubscription && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 flex items-start gap-2" data-testid="active-sub-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <b>Repeat purchase blocked</b> — you cannot buy another subscription until the current plan expires.
            Plan auto-renews are not enabled; you{`'`}ll see fresh options here once your plan ends.
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="tiers-grid">
        {tiers.map(t => {
          const cashAffordable = walletBalance ? walletBalance.cash_balance >= t.price_inr : true;
          const splitCashAff = walletBalance ? walletBalance.cash_balance >= (t.payment_split?.cash_inr || 0) : true;
          const splitTsbAff = walletBalance ? walletBalance.tsb_balance >= (t.payment_split?.tsb_required || 0) : true;
          const splitAff = splitCashAff && splitTsbAff;
          const disabledBecauseSub = !!mySubscription;
          return (
          <div key={t.id} className={`relative bg-white/5 backdrop-blur border-2 rounded-2xl p-5 ${
            t.fno_access ? 'border-pink-500/50 bg-gradient-to-br from-pink-500/10 to-rose-500/10' :
            t.id === 'yearly' ? 'border-amber-500/50 bg-gradient-to-br from-amber-500/10 to-orange-500/10' :
            'border-white/10'
          }`} data-testid={`tier-card-${t.id}`}>
            {t.fno_access && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" /> F&O PRO
              </div>
            )}
            {t.id === 'yearly' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full">BEST VALUE</div>
            )}
            <h3 className="text-lg font-bold mb-1">{t.name}</h3>
            <div className="text-3xl font-extrabold mb-1">₹{t.price_inr.toLocaleString('en-IN')}</div>
            <div className="text-xs text-slate-400 mb-3">{t.duration_days} days</div>
            <ul className="text-sm space-y-1.5 mb-4">
              <li className="flex items-center gap-2"><ChevronRight className="w-3.5 h-3.5 text-emerald-400" /> Up to {t.max_bots} bots</li>
              <li className="flex items-center gap-2"><ChevronRight className="w-3.5 h-3.5 text-emerald-400" /> Capital cap ₹{t.max_capital?.toLocaleString('en-IN')}</li>
              <li className="flex items-center gap-2"><ChevronRight className="w-3.5 h-3.5 text-emerald-400" /> 7 base strategies</li>
              {t.fno_access ? (
                <>
                  <li className="flex items-center gap-2 text-pink-300"><Crown className="w-3.5 h-3.5" /> 2 F&O strategies (Delta Neutral + Gamma Burst)</li>
                  <li className="flex items-center gap-2 text-pink-300"><ShieldCheck className="w-3.5 h-3.5" /> MTM Kill-Switch + Risk Gates</li>
                </>
              ) : (
                <li className="flex items-center gap-2"><ChevronRight className="w-3.5 h-3.5 text-emerald-400" /> Paper + Live (Phase 2)</li>
              )}
            </ul>
            <div className="bg-slate-800/50 rounded-lg p-2 mb-3 text-xs space-y-1">
              <div className="text-slate-400 font-semibold uppercase text-[10px] tracking-wider">Option A · 100% Cash</div>
              <div className="flex justify-between"><span className="text-slate-400">From Cash Wallet</span><span className={cashAffordable ? '' : 'text-red-300 font-bold'}>₹{t.price_inr?.toLocaleString('en-IN')}</span></div>
              <div className="border-t border-white/10 my-1.5"></div>
              <div className="text-slate-400 font-semibold uppercase text-[10px] tracking-wider">Option B · 50/50 Split</div>
              <div className="flex justify-between"><span className="text-slate-400">50% Cash</span><span className={splitCashAff ? '' : 'text-red-300 font-bold'}>₹{t.payment_split?.cash_inr}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">50% TSB</span><span className={splitTsbAff ? '' : 'text-red-300 font-bold'}>{t.payment_split?.tsb_required} TSB</span></div>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => requestPurchase(t, 'cash_only')}
                disabled={!!purchasing || disabledBecauseSub}
                data-testid={`buy-tier-${t.id}-cash`}
                className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title={disabledBecauseSub ? 'Active plan already running' : (cashAffordable ? '' : 'Insufficient cash')}
              >
                {purchasing === `${t.id}-cash_only` ? 'Processing…' : disabledBecauseSub ? '🔒 Active plan running' : '💵 Pay 100% Cash'}
              </button>
              <button
                onClick={() => requestPurchase(t, 'cash_tsb_50_50')}
                disabled={!!purchasing || disabledBecauseSub}
                data-testid={`buy-tier-${t.id}-split`}
                className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title={disabledBecauseSub ? 'Active plan already running' : (splitAff ? '' : 'Insufficient cash or TSB')}
              >
                {purchasing === `${t.id}-cash_tsb_50_50` ? 'Processing…' : disabledBecauseSub ? '🔒 Active plan running' : '⚖️ Pay 50% Cash + 50% TSB'}
              </button>
            </div>
          </div>
        );})}
      </div>

      {confirm && (
        <SubscriptionConfirmModal
          tier={confirm.tier}
          mode={confirm.mode}
          walletBalance={walletBalance}
          onCancel={() => setConfirm(null)}
          onConfirm={executePurchase}
        />
      )}
    </div>
  );
};

const SubscriptionConfirmModal = ({ tier, mode, walletBalance, onCancel, onConfirm }) => {
  const isCash = mode === 'cash_only';
  const cashNeeded = isCash ? tier.price_inr : tier.payment_split?.cash_inr || 0;
  const tsbNeeded = isCash ? 0 : tier.payment_split?.tsb_required || 0;
  const cashOk = walletBalance ? walletBalance.cash_balance >= cashNeeded : true;
  const tsbOk = walletBalance ? walletBalance.tsb_balance >= tsbNeeded : true;
  const canBuy = cashOk && tsbOk;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sub-confirm-modal">
      <div className="bg-slate-900 border border-amber-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-400" /> Confirm Purchase
        </h3>
        <p className="text-sm text-slate-400 mb-4">Please review charges before confirming. This action cannot be undone.</p>
        <div className="bg-slate-800/50 rounded-xl p-4 space-y-2 mb-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Plan</span><span className="font-bold">{tier.name}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Duration</span><span>{tier.duration_days} days</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Max Bots</span><span>{tier.max_bots}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Mode</span><span className="font-bold">{isCash ? '100% Cash' : '50% Cash + 50% TSB'}</span></div>
          <div className="border-t border-white/10 my-2" />
          <div className={`flex justify-between ${cashOk ? '' : 'text-red-300'}`}>
            <span>Cash debit</span>
            <span className="font-extrabold tabular-nums">₹{cashNeeded.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-[11px] text-slate-500">
            <span>Cash available</span>
            <span>₹{(walletBalance?.cash_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
          {!isCash && (
            <>
              <div className={`flex justify-between ${tsbOk ? '' : 'text-red-300'}`}>
                <span>TSB debit</span>
                <span className="font-extrabold tabular-nums">{tsbNeeded.toLocaleString('en-IN', { maximumFractionDigits: 4 })} TSB</span>
              </div>
              <div className="flex justify-between text-[11px] text-slate-500">
                <span>TSB available</span>
                <span>{(walletBalance?.tsb_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 })} TSB</span>
              </div>
            </>
          )}
          <div className="border-t border-white/10 my-2" />
          <div className="flex justify-between text-base font-bold">
            <span>Total Value</span>
            <span className="text-amber-300">₹{tier.price_inr.toLocaleString('en-IN')}</span>
          </div>
        </div>
        {!canBuy && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3 text-xs text-red-200">
            ⚠️ Insufficient {!cashOk && 'Cash'}{!cashOk && !tsbOk && ' & '}{!tsbOk && 'TSB'} balance. Please top up first.
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-slate-200" data-testid="sub-confirm-cancel">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canBuy}
            className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-extrabold text-white"
            data-testid="sub-confirm-yes"
          >
            Confirm & Pay
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Affiliate / Affiliate Network ============
const AffiliateTab = ({ summary, network, status }) => {
  const link = `${window.location.origin}/register?ref=${summary?.user_id || ''}`;
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-500/20 to-rose-500/20 border border-pink-500/30 rounded-2xl p-5">
        <h3 className="text-xl font-bold mb-1">2-Tier Affiliate Program</h3>
        <p className="text-sm text-slate-300">
          Earn <span className="font-bold text-pink-300">{status?.mlm_l1_pct || 20}%</span> from direct (Level 1) and
          <span className="font-bold text-pink-300"> {status?.mlm_l2_pct || 10}%</span> from indirect (Level 2) subscription purchases.
          Requires you to hold an <b>active subscription</b> to qualify for commissions.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        <StatCard label="Total Earnings" value={`₹${(summary?.total_earnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`} color="text-pink-300" testid="mlm-total" />
        <StatCard label="L1 Earnings" value={`₹${(summary?.l1_earnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`} testid="mlm-l1" />
        <StatCard label="L2 Earnings" value={`₹${(summary?.l2_earnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`} testid="mlm-l2" />
        <StatCard label="Team Size" value={network?.total_team_size || 0} testid="mlm-team" />
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="referral-link-card">
        <div className="text-xs text-slate-400 mb-2">Your Referral Link</div>
        <div className="flex gap-2">
          <input readOnly value={link} className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied!'); }} className="px-4 py-2 bg-amber-500 text-white rounded-lg font-semibold text-sm" data-testid="copy-ref-link">Copy</button>
        </div>
      </div>

      {network?.l1?.length > 0 && (
        <div>
          <h4 className="font-bold mb-2">Direct Referrals (L1) — {network.l1.length}</h4>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden text-sm">
            {network.l1.slice(0, 10).map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 border-t border-white/5 first:border-t-0">
                <div>
                  <div className="font-medium">{u.name || u.username || u.email}</div>
                  <div className="text-xs text-slate-500">@{u.username || u.email?.split('@')[0]}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${u.has_active_sub ? 'bg-green-500/20 text-green-300' : 'bg-slate-500/20 text-slate-400'}`}>
                  {u.has_active_sub ? '✓ Active Sub' : 'No Sub'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ F&O Desk Tab ============
const FnoDeskTab = ({ fnoAccess, bots, setActiveTab }) => {
  const [previews, setPreviews] = useState({});
  const [chain, setChain] = useState(null);
  const [realChain, setRealChain] = useState(null);   // from real Kite/NSE feed
  const [chainError, setChainError] = useState(null);
  const [symbol, setSymbol] = useState('BANKNIFTY');

  const fnoBots = bots.filter(b => b.asset_class === 'fno');

  useEffect(() => {
    if (!fnoAccess) return;
    const loadChain = async () => {
      // 1) Always load the mock ATM widget (works even without Kite) so user sees something
      try { const r = await api.get(`/algotrading/fno/option-chain/${symbol}`); setChain(r.data); } catch (e) { console.warn(e?.message); }
      // 2) Try real provider (Kite → NSE) — graceful fail if not configured
      try {
        const rr = await api.get(`/algotrading/market/option-chain/${symbol}`);
        setRealChain(rr.data); setChainError(null);
      } catch (e) {
        setRealChain(null);
        setChainError(e.response?.data?.detail || 'Live feed unavailable');
      }
    };
    loadChain();
    const id = setInterval(loadChain, 20000);
    return () => clearInterval(id);
  }, [fnoAccess, symbol]);

  useEffect(() => {
    if (!fnoAccess) return;
    fnoBots.forEach(async (b) => {
      try {
        const r = await api.get(`/algotrading/fno/preview/${b.id}`);
        setPreviews(p => ({ ...p, [b.id]: r.data }));
      } catch (e) { console.warn(e?.message); }
    });
  }, [fnoAccess, bots]);

  if (!fnoAccess) {
    return (
      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-pink-500/30 rounded-2xl p-8 text-center" data-testid="fno-locked-card">
        <Crown className="w-16 h-16 text-pink-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">F&O Pro-Plan Locked</h2>
        <p className="text-slate-300 mb-1">Access the dedicated F&O Desk with:</p>
        <ul className="text-sm text-slate-400 max-w-md mx-auto text-left list-disc list-inside mt-3 mb-5">
          <li>9:20 AM Delta Neutral (ATM Short Straddle) — 25% SL + 15:15 auto-squareoff</li>
          <li>Gamma Burst (Opening Range + VWAP Option Buying) — +20% target / -10% SL</li>
          <li>Pre-trade margin check + dynamic lot sizing</li>
          <li>MTM Kill-Switch (daily loss/profit auto-halt)</li>
          <li>Over-trading cap + slippage-control (SL-M / LIMIT only)</li>
        </ul>
        <button onClick={() => setActiveTab('plans')} className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold" data-testid="fno-desk-upgrade">
          Upgrade to F&O Pro (₹5,000/mo) →
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fno-desk">
      {/* Option Chain Widget */}
      <div className="bg-white/5 border border-pink-500/20 rounded-2xl p-5" data-testid="fno-option-chain">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Crown className="w-5 h-5 text-pink-400" /> Option Chain
            {realChain ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300" data-testid="chain-source">LIVE · {realChain.source}</span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300" data-testid="chain-source">PAPER · mock</span>
            )}
          </h3>
          <select value={symbol} onChange={e => setSymbol(e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-3 py-1.5 text-sm" data-testid="fno-chain-symbol">
            <option value="BANKNIFTY">BANKNIFTY</option>
            <option value="NIFTY">NIFTY</option>
            <option value="FINNIFTY">FINNIFTY</option>
            <option value="MIDCPNIFTY">MIDCPNIFTY</option>
          </select>
        </div>

        {/* Mock ATM Quick View */}
        {chain && (
          <div className="grid md:grid-cols-4 gap-3" data-testid="chain-atm-snapshot">
            <div className="bg-slate-800/50 rounded-lg p-3"><div className="text-xs text-slate-400">Spot</div><div className="text-xl font-bold">₹{(realChain?.underlying_value ?? chain.spot).toLocaleString('en-IN')}</div></div>
            <div className="bg-slate-800/50 rounded-lg p-3"><div className="text-xs text-slate-400">ATM Strike</div><div className="text-xl font-bold text-amber-300">{chain.atm_strike}</div></div>
            <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30"><div className="text-xs text-green-300">{chain.ce_symbol}</div><div className="text-xl font-bold text-green-300">₹{chain.ce_price}</div></div>
            <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30"><div className="text-xs text-red-300">{chain.pe_symbol}</div><div className="text-xl font-bold text-red-300">₹{chain.pe_price}</div></div>
          </div>
        )}

        {/* Real chain full table (when LIVE) */}
        {realChain && realChain.instruments?.length > 0 && (() => {
          // Group by strike, separate CE/PE
          const byStrike = {};
          realChain.instruments.forEach(i => {
            byStrike[i.strike] = byStrike[i.strike] || {};
            byStrike[i.strike][i.instrument_type] = i;
          });
          const strikes = Object.keys(byStrike).map(Number).sort((a, b) => a - b);
          // Window around ATM
          const spot = realChain.underlying_value || chain?.spot || 0;
          const atmStrike = strikes.reduce((p, c) => Math.abs(c - spot) < Math.abs(p - spot) ? c : p, strikes[0]);
          const atmIdx = strikes.indexOf(atmStrike);
          const window = strikes.slice(Math.max(0, atmIdx - 5), Math.min(strikes.length, atmIdx + 6));
          return (
            <div className="mt-4 overflow-x-auto" data-testid="chain-full-table">
              <div className="text-xs text-slate-400 mb-2">Expiry: <b className="text-white">{realChain.expiry}</b> · Strikes shown: ATM ±5 · LTP/OI/IV</div>
              <table className="w-full text-xs">
                <thead className="text-slate-400 border-b border-white/10">
                  <tr>
                    <th className="p-2 text-right text-green-300" colSpan={3}>CALL (CE)</th>
                    <th className="p-2 text-center">STRIKE</th>
                    <th className="p-2 text-left text-red-300" colSpan={3}>PUT (PE)</th>
                  </tr>
                  <tr className="text-slate-500">
                    <th className="p-1 text-right">OI</th><th className="p-1 text-right">IV%</th><th className="p-1 text-right">LTP</th>
                    <th className="p-1 text-center"></th>
                    <th className="p-1 text-left">LTP</th><th className="p-1 text-left">IV%</th><th className="p-1 text-left">OI</th>
                  </tr>
                </thead>
                <tbody>
                  {window.map(s => {
                    const ce = byStrike[s]?.CE; const pe = byStrike[s]?.PE;
                    const isAtm = s === atmStrike;
                    return (
                      <tr key={s} className={`border-b border-white/5 ${isAtm ? 'bg-amber-500/10' : 'hover:bg-white/5'}`} data-testid={`chain-row-${s}`}>
                        <td className="p-1.5 text-right text-slate-300">{ce?.oi?.toLocaleString('en-IN') || '—'}</td>
                        <td className="p-1.5 text-right text-slate-300">{ce?.iv ? Number(ce.iv).toFixed(1) : '—'}</td>
                        <td className="p-1.5 text-right text-green-300 font-bold">{ce?.ltp ? `₹${ce.ltp.toFixed(2)}` : '—'}</td>
                        <td className={`p-1.5 text-center font-bold ${isAtm ? 'text-amber-300' : 'text-white'}`}>{s}</td>
                        <td className="p-1.5 text-left text-red-300 font-bold">{pe?.ltp ? `₹${pe.ltp.toFixed(2)}` : '—'}</td>
                        <td className="p-1.5 text-left text-slate-300">{pe?.iv ? Number(pe.iv).toFixed(1) : '—'}</td>
                        <td className="p-1.5 text-left text-slate-300">{pe?.oi?.toLocaleString('en-IN') || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {realChain.all_expiries?.length > 1 && (
                <div className="text-xs text-slate-500 mt-2">Other expiries: {realChain.all_expiries.slice(1, 5).join(' · ')}</div>
              )}
            </div>
          );
        })()}

        {/* Connect-for-live CTA when chain is mock */}
        {!realChain && chainError && (
          <div className="mt-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm" data-testid="chain-connect-cta">
            <div className="font-semibold text-blue-300">📡 Currently showing PAPER (mock) data</div>
            <div className="text-xs text-slate-400 mt-1">{chainError}</div>
            <div className="text-xs text-slate-300 mt-2">For LIVE option chain: connect <b>Zerodha Kite</b> (any user) or have admin connect <b>Upstox</b> in admin panel → Market Data tab.</div>
          </div>
        )}
        <p className="text-xs text-slate-500 mt-3">Lot size: {chain?.lot_size} · IV: {chain ? (chain.iv * 100).toFixed(0) : '—'}% · DTE: {chain?.days_to_expiry} · Updates every 20s</p>
      </div>

      {/* My F&O Bots */}
      <div>
        <h3 className="text-lg font-bold mb-3">My F&O Bots</h3>
        {fnoBots.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center text-slate-400" data-testid="fno-empty-state">
            No F&amp;O bots yet. Head to <button onClick={() => setActiveTab('strategies')} className="text-pink-400 hover:underline">Strategies</button> and deploy &quot;9:20 Delta Neutral&quot; or &quot;Gamma Burst&quot;.
          </div>
        ) : (
          <div className="space-y-3">
            {fnoBots.map(bot => {
              const p = previews[bot.id];
              return (
                <div key={bot.id} className="bg-white/5 border border-pink-500/20 rounded-2xl p-5" data-testid={`fno-bot-${bot.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-bold flex items-center gap-2">{bot.strategy_name} {bot.is_active ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-300">ACTIVE</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">PAUSED</span>}</h4>
                      <div className="text-xs text-slate-400 mt-1">
                        Lots: {bot.fno_lots} · Loss limit: ₹{bot.fno_daily_loss_limit || '—'} · Profit target: ₹{bot.fno_daily_profit_target || '—'} · Max trades: {bot.fno_max_trades_per_day || '—'}/day
                      </div>
                      {bot.paused_reason && (
                        <div className="text-xs text-red-300 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {bot.paused_reason}</div>
                      )}
                    </div>
                  </div>
                  {p && p.strategy === 'delta_neutral_920' && (
                    <div className="grid md:grid-cols-3 gap-3 text-sm">
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <div className="text-xs text-slate-400">ATM Strike</div>
                        <div className="text-lg font-bold">{p.atm_strike}</div>
                        <div className="text-xs text-slate-500 mt-1">Entry: {p.entry_time_ist} IST · Exit: {p.exit_time_ist} IST</div>
                      </div>
                      <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30">
                        <div className="text-xs text-green-300">SELL {p.ce.symbol}</div>
                        <div className="text-lg font-bold">₹{p.ce.price} × {p.ce.qty}</div>
                        <div className="text-xs text-slate-400">SL @ ₹{p.ce.stop_loss_price} (+25%)</div>
                      </div>
                      <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                        <div className="text-xs text-red-300">SELL {p.pe.symbol}</div>
                        <div className="text-lg font-bold">₹{p.pe.price} × {p.pe.qty}</div>
                        <div className="text-xs text-slate-400">SL @ ₹{p.pe.stop_loss_price} (+25%)</div>
                      </div>
                      <div className="md:col-span-3 bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
                        <div className="flex justify-between text-sm">
                          <span>Premium Received: <b className="text-amber-300">₹{p.premium_received_inr?.toLocaleString('en-IN')}</b></span>
                          <span>Max Profit: <b className="text-green-300">₹{p.max_profit_inr?.toLocaleString('en-IN')}</b></span>
                        </div>
                      </div>
                    </div>
                  )}
                  {p && p.strategy === 'gamma_burst' && (
                    <div className="space-y-3 text-sm">
                      <div className="grid md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-slate-800/50 rounded p-2"><div className="text-slate-400">Spot</div><div className="font-bold">₹{p.spot}</div></div>
                        <div className="bg-slate-800/50 rounded p-2"><div className="text-slate-400">15-min H</div><div className="font-bold">₹{p.h15}</div></div>
                        <div className="bg-slate-800/50 rounded p-2"><div className="text-slate-400">15-min L</div><div className="font-bold">₹{p.l15}</div></div>
                        <div className="bg-slate-800/50 rounded p-2"><div className="text-slate-400">VWAP</div><div className="font-bold">₹{p.vwap}</div></div>
                      </div>
                      <div className={`p-3 rounded-lg border ${
                        p.signal === 'BUY_CE' ? 'bg-green-500/10 border-green-500/40 text-green-300' :
                        p.signal === 'BUY_PE' ? 'bg-red-500/10 border-red-500/40 text-red-300' :
                        'bg-slate-700/30 border-slate-600 text-slate-400'
                      }`}>
                        <div className="font-bold">Current Signal: {p.signal}</div>
                        {p.trade && (
                          <div className="text-xs mt-1">
                            {p.trade.side} {p.trade.symbol} × {p.trade.qty} @ ₹{p.trade.price} · Target ₹{p.trade.target_price} · SL ₹{p.trade.stop_loss_price}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {p && p.risk_check && !p.risk_check.ok && (
                    <div className="mt-3 bg-red-500/20 border border-red-500/40 rounded-lg p-2 text-xs text-red-300 flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3" /> Risk Gate: {p.risk_check.reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Live Ticker WebSocket Hook ============
const useTickerStream = (enabled = true) => {
  const [stocks, setStocks] = useState({});
  const [fno, setFno] = useState({});
  const [status, setStatus] = useState('disconnected');
  const wsRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws/ticker';
    const token = localStorage.getItem('token');
    if (!token) return;

    let ws;
    let pingTimer;
    let retryTimer;
    let closed = false;

    const connect = () => {
      ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus('connected');
        // Subscribe to all (empty arrays = all)
        ws.send(JSON.stringify({ action: 'subscribe', stocks: [], fno: [] }));
        // Heartbeat
        pingTimer = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ action: 'ping' }));
        }, 25000);
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'tick') {
            if (data.stocks) setStocks(data.stocks);
            if (data.fno) setFno(data.fno);
          }
        } catch (err) { /* noop */ }
      };
      ws.onclose = () => {
        setStatus('disconnected');
        clearInterval(pingTimer);
        if (!closed) {
          retryTimer = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => {
        setStatus('error');
      };
    };
    connect();

    return () => {
      closed = true;
      clearInterval(pingTimer);
      clearTimeout(retryTimer);
      if (ws && ws.readyState === 1) ws.close();
    };
  }, [enabled]);

  return { stocks, fno, status };
};

// ============ Backtest Tab ============
const BacktestTab = ({ strategies, stocks }) => {
  const [form, setForm] = useState({
    strategy_id: 'trend_rider',
    symbol: 'RELIANCE',
    capital_inr: 500000,
    start_date: '2024-01-01',
    end_date: '2025-01-01',
    seed: 42,
  });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post('/algotrading/backtest/run', form, { timeout: 30000 });
      setResult(r.data);
      toast.success('Backtest complete ✓');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backtest failed');
    } finally { setRunning(false); }
  };

  // SVG sparkline for equity curve
  const renderChart = () => {
    if (!result?.equity_curve?.length) return null;
    const values = result.equity_curve.map(p => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const w = 800;
    const h = 200;
    const points = values.map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const startCapital = form.capital_inr;
    const endValue = values[values.length - 1];
    const profitable = endValue >= startCapital;
    const yZero = h - ((startCapital - min) / range) * h;
    return (
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-48 mt-2">
        <line x1="0" y1={yZero} x2={w} y2={yZero} stroke="#64748b" strokeDasharray="4 4" strokeWidth="1" />
        <polyline points={points} fill="none" stroke={profitable ? '#34d399' : '#f87171'} strokeWidth="2" />
        <text x="10" y="20" fill="#94a3b8" fontSize="12">Min ₹{min.toLocaleString('en-IN', {maximumFractionDigits:0})}</text>
        <text x={w - 10} y="20" fill="#94a3b8" fontSize="12" textAnchor="end">Max ₹{max.toLocaleString('en-IN', {maximumFractionDigits:0})}</text>
      </svg>
    );
  };

  const m = result?.metrics;

  return (
    <div className="space-y-5" data-testid="backtest-tab">
      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-2xl p-5">
        <h3 className="text-xl font-bold flex items-center gap-2"><LineChart className="w-5 h-5 text-blue-400" /> What-If Backtest Simulator</h3>
        <p className="text-sm text-slate-300 mt-1">Replay any strategy on historical mock prices. Get equity curve, max drawdown, Sharpe ratio, win-rate. Bridges paper trading to live with confidence.</p>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="backtest-form">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Strategy</label>
            <select value={form.strategy_id} onChange={e => setForm({...form, strategy_id: e.target.value})} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="bt-strategy">
              {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Symbol</label>
            <select value={form.symbol} onChange={e => setForm({...form, symbol: e.target.value})} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="bt-symbol">
              {stocks.map(s => <option key={s.symbol} value={s.symbol}>{s.symbol}</option>)}
              <option value="BANKNIFTY">BANKNIFTY (F&O)</option>
              <option value="NIFTY">NIFTY (F&O)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Capital (₹)</label>
            <input type="number" value={form.capital_inr} onChange={e => setForm({...form, capital_inr: parseFloat(e.target.value) || 0})} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="bt-capital" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Start Date</label>
            <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="bt-start" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">End Date</label>
            <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="bt-end" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Random Seed (optional)</label>
            <input type="number" value={form.seed} onChange={e => setForm({...form, seed: parseInt(e.target.value, 10) || 0})} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="bt-seed" />
          </div>
        </div>
        <button onClick={run} disabled={running} className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="bt-run">
          {running ? <><RefreshCw className="w-4 h-4 animate-spin" /> Running…</> : <><LineChart className="w-4 h-4" /> Run Backtest</>}
        </button>
      </div>

      {result && (
        <div className="space-y-4" data-testid="backtest-results">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs text-slate-400 uppercase">{result.strategy_name} · {result.symbol}</div>
                <div className="text-sm text-slate-300">{result.start_date} → {result.end_date}</div>
              </div>
              <div className={`text-right ${m.total_pnl_inr >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                <div className="text-3xl font-extrabold">{m.total_return_pct >= 0 ? '+' : ''}{m.total_return_pct}%</div>
                <div className="text-sm">₹{m.total_pnl_inr.toLocaleString('en-IN')}</div>
              </div>
            </div>
            {renderChart()}
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <MetricCard label="Sharpe Ratio" value={m.sharpe_ratio} color={m.sharpe_ratio >= 1 ? 'text-emerald-300' : m.sharpe_ratio >= 0 ? 'text-amber-300' : 'text-red-300'} testid="bt-sharpe" />
            <MetricCard label="Max Drawdown" value={`${m.max_drawdown_pct}%`} color="text-red-300" testid="bt-drawdown" />
            <MetricCard label="CAGR" value={`${m.cagr_pct}%`} color={m.cagr_pct >= 0 ? 'text-emerald-300' : 'text-red-300'} testid="bt-cagr" />
            <MetricCard label="Win Rate" value={`${m.win_rate_pct}%`} testid="bt-winrate" />
            <MetricCard label="Total Trades" value={m.total_trades} testid="bt-trades" />
            <MetricCard label="Winning Trades" value={`${m.winning_trades}/${m.completed_trades}`} testid="bt-wins" />
            <MetricCard label="Ending Capital" value={`₹${m.ending_capital.toLocaleString('en-IN')}`} testid="bt-ending" />
            <MetricCard label="Trading Days" value={m.trading_days} testid="bt-days" />
          </div>
          <details className="bg-slate-900/50 border border-white/10 rounded-xl p-4 text-xs">
            <summary className="cursor-pointer text-slate-300 font-medium">📊 Trade-by-Trade Log ({result.trades?.length || 0})</summary>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[480px]">
                <thead className="text-slate-400"><tr><th className="text-left p-2">Date</th><th className="p-2">Side</th><th className="text-right p-2">Price</th><th className="text-right p-2">Qty</th><th className="text-right p-2">P&L</th></tr></thead>
                <tbody>
                  {(result.trades || []).map((t, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-2">{t.date}</td>
                      <td className={`p-2 text-center ${t.side === 'buy' ? 'text-green-300' : 'text-red-300'}`}>{t.side.toUpperCase()}</td>
                      <td className="p-2 text-right">₹{t.price}</td>
                      <td className="p-2 text-right">{t.qty}</td>
                      <td className={`p-2 text-right ${t.pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{t.pnl ? `₹${t.pnl.toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, color = 'text-white', testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={testid}>
    <div className="text-xs text-slate-400 mb-1">{label}</div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
  </div>
);

// ============ AI Trading Coach (Claude Sonnet 4.6) ============
const AICoachTab = ({ fnoAccess, setActiveTab }) => {
  const [section, setSection] = useState('analysis'); // 'analysis' | 'recommender'
  const [analysis, setAnalysis] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cached, setCached] = useState(false);

  // Strategy Recommender state
  const [profile, setProfile] = useState({
    capital_inr: 500000,
    monthly_income_inr: 80000,
    experience_years: 2,
    risk_appetite: 'moderate',
    max_drawdown_tolerance_pct: 15,
    preferred_segments: ['stocks'],
    holding_horizon: 'swing',
    goal: 'Build steady wealth',
    has_full_time_job: true,
  });
  const [recommendation, setRecommendation] = useState(null);
  const [recommending, setRecommending] = useState(false);

  const runAnalysis = async (force = false) => {
    setLoading(true);
    try {
      const r = await api.post('/algotrading/ai-coach/analyze', { force_refresh: force }, { timeout: 60000 });
      setAnalysis(r.data.analysis);
      setSummary(r.data.summary);
      setCached(!!r.data.cached);
      if (r.data.cached) toast.info('Showing cached analysis (refresh after 1h)');
      else toast.success('AI Coach analysis ready ✓');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI Coach failed');
    } finally { setLoading(false); }
  };

  const runRecommend = async () => {
    setRecommending(true);
    try {
      const r = await api.post('/algotrading/ai-coach/recommend', profile, { timeout: 60000 });
      setRecommendation(r.data.recommendation);
      toast.success('Strategy recommendation ready ✓');
      // Scroll results into view after render
      setTimeout(() => {
        const el = document.querySelector('[data-testid="recommendation-results"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recommender failed');
    } finally { setRecommending(false); }
  };

  useEffect(() => {
    if (fnoAccess && section === 'analysis' && !analysis && !loading) runAnalysis(false);
  }, [fnoAccess, section]);

  const toggleSegment = (seg) => {
    setProfile(p => ({
      ...p,
      preferred_segments: p.preferred_segments.includes(seg)
        ? p.preferred_segments.filter(s => s !== seg)
        : [...p.preferred_segments, seg]
    }));
  };

  if (!fnoAccess) {
    return (
      <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-purple-500/30 rounded-2xl p-8 text-center" data-testid="ai-coach-locked">
        <Brain className="w-16 h-16 text-purple-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">AI Trading Coach + Strategy Recommender</h2>
        <p className="text-slate-300 mb-2">Powered by <b>Claude Sonnet 4.6</b> — Two AI tools in one:</p>
        <ul className="text-sm text-slate-400 max-w-md mx-auto text-left list-disc list-inside mt-3 mb-5">
          <li><b>AI Coach</b> — analyzes your last 30 paper trades, scores 1-10, Hindi+English verdict</li>
          <li><b>Strategy Recommender</b> — risk profile questionnaire → personalized strategy match + capital allocation plan</li>
          <li>Risk warnings (over-trading, revenge trading, etc.)</li>
          <li>Best-fit strategy from 9 catalog options</li>
          <li>F&O eligibility check based on capital + experience</li>
        </ul>
        <button onClick={() => setActiveTab('plans')} className="px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-bold" data-testid="ai-coach-upgrade">
          Unlock with F&O Pro (₹5,000/mo) →
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ai-coach-tab">
      {/* Sub-section selector */}
      <div className="flex gap-2 bg-white/5 border border-white/10 rounded-xl p-1 w-fit">
        <button
          onClick={() => setSection('analysis')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${section === 'analysis' ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white' : 'text-slate-400 hover:text-white'}`}
          data-testid="ai-coach-section-analysis"
        >
          <Brain className="w-4 h-4 inline mr-1" /> Trade Analysis
        </button>
        <button
          onClick={() => setSection('recommender')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${section === 'recommender' ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white' : 'text-slate-400 hover:text-white'}`}
          data-testid="ai-coach-section-recommender"
        >
          <Cpu className="w-4 h-4 inline mr-1" /> Strategy Recommender
        </button>
      </div>

      {section === 'analysis' && (
        <>
          <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-purple-500/30 rounded-2xl p-5 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2"><Brain className="w-5 h-5 text-purple-400" /> AI Trading Coach</h3>
              <p className="text-sm text-slate-300 mt-1">Personalized analysis powered by Claude Sonnet 4.6. {cached && <span className="text-amber-300">(Cached — refresh after 1h)</span>}</p>
            </div>
            <button onClick={() => runAnalysis(true)} disabled={loading} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50" data-testid="ai-coach-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Analyzing…' : 'Re-analyze'}
            </button>
          </div>

          {loading && !analysis && (
            <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl">
              <Brain className="w-12 h-12 text-purple-400 mx-auto mb-3 animate-pulse" />
              <div className="text-lg font-bold mb-1">Coach is thinking…</div>
              <div className="text-sm text-slate-400">Analyzing your last 30 trades with Claude Sonnet 4.6</div>
            </div>
          )}

          {analysis && (
            <>
              <div className="bg-white/5 border border-purple-500/20 rounded-2xl p-6" data-testid="ai-coach-score">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider">Overall Trading Score</div>
                    <div className="flex items-baseline gap-2">
                      <div className={`text-6xl font-extrabold ${
                        analysis.overall_score >= 8 ? 'text-emerald-300' :
                        analysis.overall_score >= 6 ? 'text-amber-300' :
                        analysis.overall_score >= 4 ? 'text-orange-300' : 'text-red-300'
                      }`}>{analysis.overall_score}</div>
                      <div className="text-2xl text-slate-500">/10</div>
                    </div>
                  </div>
                  <Sparkles className={`w-16 h-16 ${
                    analysis.overall_score >= 8 ? 'text-emerald-400' :
                    analysis.overall_score >= 6 ? 'text-amber-400' : 'text-orange-400'
                  }`} />
                </div>
                <div className="text-lg font-medium text-white mt-2">{analysis.verdict}</div>
                {analysis.verdict_hi && <div className="text-sm text-slate-400 mt-1">{analysis.verdict_hi}</div>}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5" data-testid="ai-coach-strengths">
                  <h4 className="font-bold text-emerald-300 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Strengths</h4>
                  <ul className="space-y-2">
                    {(analysis.strengths || []).map((s, i) => (<li key={i} className="flex items-start gap-2 text-sm"><span className="text-emerald-400 mt-0.5">✓</span> {s}</li>))}
                  </ul>
                </div>
                <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5" data-testid="ai-coach-weaknesses">
                  <h4 className="font-bold text-red-300 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Weaknesses</h4>
                  <ul className="space-y-2">
                    {(analysis.weaknesses || []).map((w, i) => (<li key={i} className="flex items-start gap-2 text-sm"><span className="text-red-400 mt-0.5">⚠</span> {w}</li>))}
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-5" data-testid="ai-coach-recommendation">
                <h4 className="font-bold text-amber-300 mb-2 flex items-center gap-2"><Zap className="w-4 h-4" /> Top Recommendation</h4>
                <p className="text-white">{analysis.top_recommendation}</p>
              </div>

              {analysis.suggested_strategy_id && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-5" data-testid="ai-coach-suggested">
                  <h4 className="font-bold text-purple-300 mb-2 flex items-center gap-2"><Cpu className="w-4 h-4" /> Best-Fit Strategy</h4>
                  <div className="text-white font-medium capitalize">{analysis.suggested_strategy_id.replace(/_/g, ' ')}</div>
                  <p className="text-sm text-slate-400 mt-1">{analysis.suggested_strategy_reason}</p>
                  <button onClick={() => setActiveTab('strategies')} className="mt-3 text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="ai-coach-go-strategy">
                    Explore strategies <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}

              {analysis.risk_warning && analysis.risk_warning.length > 5 && (
                <div className="bg-red-500/20 border-2 border-red-500/40 rounded-2xl p-4 flex items-start gap-3" data-testid="ai-coach-risk">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <div className="font-bold text-red-300">Risk Alert</div>
                    <p className="text-sm text-red-200/90 mt-1">{analysis.risk_warning}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {section === 'recommender' && (
        <>
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-5">
            <h3 className="text-xl font-bold flex items-center gap-2"><Cpu className="w-5 h-5 text-purple-400" /> AI Strategy Recommender</h3>
            <p className="text-sm text-slate-300 mt-1">Fill your risk profile — Claude Sonnet 4.6 will recommend the best strategy match + capital allocation plan + F&O eligibility check.</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4" data-testid="recommender-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Trading Capital (₹)</label>
                <input type="number" value={profile.capital_inr} onChange={e => setProfile({ ...profile, capital_inr: parseFloat(e.target.value) || 0 })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-capital" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Monthly Income (₹)</label>
                <input type="number" value={profile.monthly_income_inr} onChange={e => setProfile({ ...profile, monthly_income_inr: parseFloat(e.target.value) || 0 })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-income" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Years of Trading Experience</label>
                <input type="number" step="0.5" value={profile.experience_years} onChange={e => setProfile({ ...profile, experience_years: parseFloat(e.target.value) || 0 })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-experience" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Risk Appetite</label>
                <select value={profile.risk_appetite} onChange={e => setProfile({ ...profile, risk_appetite: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-risk">
                  <option value="conservative">Conservative</option>
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Max Drawdown Tolerance (%)</label>
                <input type="number" value={profile.max_drawdown_tolerance_pct} onChange={e => setProfile({ ...profile, max_drawdown_tolerance_pct: parseFloat(e.target.value) || 0 })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-drawdown" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Holding Horizon</label>
                <select value={profile.holding_horizon} onChange={e => setProfile({ ...profile, holding_horizon: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-horizon">
                  <option value="intraday">Intraday</option>
                  <option value="swing">Swing (3-10d)</option>
                  <option value="positional">Positional (weeks/months)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Preferred Segments</label>
              <div className="flex flex-wrap gap-2">
                {['stocks', 'crypto', 'fno', 'forex'].map(seg => (
                  <button key={seg} type="button" onClick={() => toggleSegment(seg)} data-testid={`profile-seg-${seg}`}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${profile.preferred_segments.includes(seg) ? 'bg-purple-500/30 text-white border-purple-400' : 'bg-slate-800 text-slate-400 border-white/10'}`}>
                    {seg.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Your Goal</label>
              <input type="text" value={profile.goal} onChange={e => setProfile({ ...profile, goal: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="profile-goal" placeholder="e.g. 25% annual returns" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={profile.has_full_time_job} onChange={e => setProfile({ ...profile, has_full_time_job: e.target.checked })} className="w-4 h-4" data-testid="profile-job" />
              I have a full-time job (limits intraday strategies)
            </label>
            <button onClick={runRecommend} disabled={recommending} className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="recommender-submit">
              {recommending ? (<><RefreshCw className="w-4 h-4 animate-spin" /> Claude is analyzing…</>) : (<><Sparkles className="w-4 h-4" /> Get AI Recommendation</>)}
            </button>
          </div>

          {recommendation && (
            <div className="space-y-4" data-testid="recommendation-results">
              <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-2 border-emerald-500/40 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs text-emerald-300 uppercase tracking-wider">Primary Recommendation</div>
                    <div className="text-2xl font-bold capitalize">{recommendation.primary_recommendation?.strategy_id?.replace(/_/g, ' ')}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-extrabold text-emerald-300">{recommendation.primary_recommendation?.match_score}<span className="text-lg text-slate-400">/100</span></div>
                    <div className="text-xs text-slate-400">match score</div>
                  </div>
                </div>
                <p className="text-sm text-slate-300 mt-2">{recommendation.primary_recommendation?.reason}</p>
                <div className="text-xs text-slate-400 mt-2">Expected volatility: <b className="text-white">{recommendation.primary_recommendation?.expected_volatility}</b> · Expected drawdown: <b className="text-white">{recommendation.primary_recommendation?.expected_drawdown_pct}%</b></div>
              </div>

              {recommendation.secondary_recommendation && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-5">
                  <div className="text-xs text-blue-300 uppercase tracking-wider">Secondary Pick</div>
                  <div className="text-lg font-bold capitalize">{recommendation.secondary_recommendation.strategy_id?.replace(/_/g, ' ')}</div>
                  <p className="text-sm text-slate-300 mt-1">{recommendation.secondary_recommendation.reason}</p>
                </div>
              )}

              {recommendation.capital_allocation_plan && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5">
                  <h4 className="font-bold text-amber-300 mb-3">Capital Allocation Plan</h4>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center bg-slate-800/50 rounded-lg p-3">
                      <div className="text-2xl font-bold text-emerald-300">{recommendation.capital_allocation_plan.primary_pct}%</div>
                      <div className="text-xs text-slate-400 mt-1">Primary</div>
                    </div>
                    <div className="text-center bg-slate-800/50 rounded-lg p-3">
                      <div className="text-2xl font-bold text-blue-300">{recommendation.capital_allocation_plan.secondary_pct}%</div>
                      <div className="text-xs text-slate-400 mt-1">Secondary</div>
                    </div>
                    <div className="text-center bg-slate-800/50 rounded-lg p-3">
                      <div className="text-2xl font-bold text-slate-300">{recommendation.capital_allocation_plan.cash_buffer_pct}%</div>
                      <div className="text-xs text-slate-400 mt-1">Cash Buffer</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300">{recommendation.capital_allocation_plan.notes}</p>
                </div>
              )}

              {recommendation.avoid_strategies?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5">
                  <h4 className="font-bold text-red-300 mb-2">⚠ Avoid These Strategies</h4>
                  <ul className="space-y-1.5 text-sm">
                    {recommendation.avoid_strategies.map((a, i) => (
                      <li key={i}><span className="font-medium capitalize">{a.strategy_id?.replace(/_/g, ' ')}</span> — <span className="text-slate-400">{a.reason}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {recommendation.fno_eligibility && (
                <div className={`border rounded-2xl p-4 ${recommendation.fno_eligibility.eligible ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-500/10 border-slate-500/30'}`}>
                  <div className="font-bold flex items-center gap-2">
                    <Crown className="w-4 h-4" /> F&O Eligibility: {recommendation.fno_eligibility.eligible ? '✓ Eligible' : '✗ Not yet'}
                  </div>
                  <p className="text-sm text-slate-300 mt-1">{recommendation.fno_eligibility.reason}</p>
                </div>
              )}

              {(recommendation.next_steps_en || recommendation.next_steps_hi) && (
                <div className="grid md:grid-cols-2 gap-4">
                  {recommendation.next_steps_en && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                      <h4 className="font-bold mb-2">Next Steps (English)</h4>
                      <ol className="space-y-2 text-sm">
                        {recommendation.next_steps_en.map((s, i) => (<li key={i}><b className="text-purple-300">{i + 1}.</b> {s}</li>))}
                      </ol>
                    </div>
                  )}
                  {recommendation.next_steps_hi && (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                      <h4 className="font-bold mb-2">अगले कदम (हिंदी)</h4>
                      <ol className="space-y-2 text-sm">
                        {recommendation.next_steps_hi.map((s, i) => (<li key={i}><b className="text-purple-300">{i + 1}.</b> {s}</li>))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============ Leaderboard ============
const LeaderboardTab = ({ data, reload }) => {
  if (!data) return <div className="text-slate-400">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="leaderboard-tab">
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-5 flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Paper-Trading Leaderboard</h3>
          <p className="text-sm text-slate-300 mt-1">Top {data.leaderboard?.length} traders ranked by realized P&L · {data.total_traders} total · Your rank: <b className="text-amber-300">#{data.your_rank || '—'}</b></p>
        </div>
        <button onClick={reload} className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-1" data-testid="leaderboard-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400">
            <tr><th className="p-3 text-left w-16">Rank</th><th className="p-3 text-left">Trader</th><th className="p-3 text-right">Realized P&L</th><th className="p-3 text-right">Trades</th><th className="p-3 text-right">Win-Rate</th></tr>
          </thead>
          <tbody>
            {(data.leaderboard || []).map((e, i) => (
              <tr key={i} className={`border-t border-white/5 ${e.is_current_user ? 'bg-amber-500/10' : ''}`} data-testid={`leaderboard-row-${i}`}>
                <td className="p-3">
                  <div className={`text-lg font-bold ${
                    i === 0 ? 'text-amber-300' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-orange-400' : 'text-slate-500'
                  }`}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</div>
                </td>
                <td className="p-3">
                  <span className="font-medium">{e.display_name}</span>
                  {e.is_current_user && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-300">YOU</span>}
                </td>
                <td className={`p-3 text-right font-bold ${e.realized_pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                  ₹{e.realized_pnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </td>
                <td className="p-3 text-right">{e.total_trades}</td>
                <td className="p-3 text-right">{e.win_rate_pct}%</td>
              </tr>
            ))}
            {(!data.leaderboard || data.leaderboard.length === 0) && (
              <tr><td colSpan="5" className="p-6 text-center text-slate-400">No traders yet — be the first!</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
// =================== Cash Deposit Tab ===================
const DepositTab = ({ walletBalance, reload }) => {
  const [settings, setSettings] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: '', method: 'upi', txn_ref: '', payer_name: '', payer_upi_or_account: '', screenshot_url: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/algotrading/deposit-settings'),
        api.get('/algotrading/deposits/my'),
      ]);
      setSettings(a.data.settings);
      setDeposits(b.data.deposits || []);
    } catch (e) { console.warn(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const submit = async () => {
    if (!form.amount || !form.txn_ref) { toast.error('Amount and Transaction Ref are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/algotrading/deposits/create', {
        ...form,
        amount: parseFloat(form.amount),
      });
      toast.success('Deposit request submitted. Awaiting admin verification.', { style: { background: '#065f46', color: '#ecfdf5' } });
      setForm({ amount: '', method: 'upi', txn_ref: '', payer_name: '', payer_upi_or_account: '', screenshot_url: '', notes: '' });
      loadAll();
      reload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const handleCopy = (text) => {
    try { navigator.clipboard.writeText(text); toast.success('Copied!'); } catch (_) { /* ignore */ }
  };

  if (loading || !settings) return <div className="text-center text-slate-400 py-10">Loading deposit info…</div>;

  if (!settings.is_active) {
    return <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 text-amber-200 text-center" data-testid="deposit-disabled">
      <Wallet className="w-10 h-10 mx-auto mb-2 opacity-60" />
      <h3 className="font-bold text-lg mb-1">Cash deposits are temporarily paused</h3>
      <p className="text-sm">Please contact admin or try again later.</p>
    </div>;
  }

  const renderRow = (label, value, testid) => (
    <div className="flex items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0" data-testid={testid}>
      <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold">{value}</span>
        <button onClick={() => handleCopy(value)} className="px-2 py-0.5 text-[10px] bg-white/5 hover:bg-white/10 rounded text-amber-300">COPY</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Current cash balance reminder */}
      {walletBalance && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center justify-between" data-testid="deposit-current-cash">
          <span className="text-sm text-emerald-200">Current Cash Balance</span>
          <span className="text-xl font-extrabold text-emerald-200">₹{walletBalance.cash_balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Payment Destination Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="deposit-payment-info">
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-400" /> Pay to
          </h3>
          {/* UPI Section */}
          <div className="bg-slate-800/40 rounded-xl p-3 mb-3" data-testid="deposit-upi">
            <div className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-2">UPI Transfer</div>
            {renderRow('UPI ID', settings.upi_id, 'row-upi-id')}
            {settings.upi_qr_url && (
              <div className="mt-3 text-center">
                <img src={settings.upi_qr_url} alt="UPI QR" className="mx-auto max-w-[160px] rounded-lg border border-white/10" />
                <div className="text-[10px] text-slate-500 mt-1">Scan to pay</div>
              </div>
            )}
          </div>
          {/* Bank Section */}
          <div className="bg-slate-800/40 rounded-xl p-3" data-testid="deposit-bank">
            <div className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-2">Bank Transfer (NEFT / IMPS / RTGS)</div>
            {renderRow('Account Name', settings.bank_account_name, 'row-bank-name')}
            {renderRow('Account Number', settings.bank_account_number, 'row-bank-acc')}
            {renderRow('IFSC', settings.bank_ifsc, 'row-bank-ifsc')}
            {renderRow('Bank', settings.bank_name, 'row-bank-branch')}
          </div>
          <div className="mt-3 text-xs text-slate-400 italic">{settings.instructions}</div>
          <div className="mt-2 text-[11px] text-slate-500">
            Range: ₹{settings.min_amount?.toLocaleString('en-IN')} – ₹{settings.max_amount?.toLocaleString('en-IN')}
          </div>
        </div>

        {/* Submission Form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="deposit-form">
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <span className="text-emerald-400">✓</span> After Paying — Submit Proof
          </h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Amount (₹)</label>
                <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="500" data-testid="deposit-amount" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Method</label>
                <select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="deposit-method">
                  <option value="upi">UPI</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Transaction Reference / UTR *</label>
              <input value={form.txn_ref} onChange={e => setForm({ ...form, txn_ref: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 423098765432" data-testid="deposit-txnref" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Payer Name (optional)</label>
              <input value={form.payer_name} onChange={e => setForm({ ...form, payer_name: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Name on bank statement" data-testid="deposit-payer-name" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Your UPI / Account # (optional)</label>
              <input value={form.payer_upi_or_account} onChange={e => setForm({ ...form, payer_upi_or_account: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 9876@upi" data-testid="deposit-payer-acc" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Screenshot URL (optional)</label>
              <input value={form.screenshot_url} onChange={e => setForm({ ...form, screenshot_url: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="https://…" data-testid="deposit-screenshot" />
              <div className="text-[10px] text-slate-500 mt-1">Paste a public image link (Imgur, Drive, etc.)</div>
            </div>
            <button onClick={submit} disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-extrabold rounded-xl disabled:opacity-50"
              data-testid="deposit-submit">
              {submitting ? 'Submitting…' : '🧾 Submit Deposit Request'}
            </button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="deposit-history">
        <h3 className="text-lg font-bold mb-3">📋 My Deposit History</h3>
        {deposits.length === 0 ? (
          <div className="text-center text-slate-500 py-8 text-sm">No deposits yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-white/5 text-xs uppercase text-slate-400">
                <tr><th className="p-2 text-left">Date</th><th className="p-2 text-right">Amount</th><th className="p-2">Method</th><th className="p-2 text-left">Txn Ref</th><th className="p-2 text-center">Status</th></tr>
              </thead>
              <tbody>
                {deposits.map(d => (
                  <tr key={d.id} className="border-t border-white/5">
                    <td className="p-2 text-xs">{new Date(d.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="p-2 text-right font-bold">₹{d.amount.toLocaleString('en-IN')}</td>
                    <td className="p-2 text-center"><span className="text-xs uppercase">{d.method}</span></td>
                    <td className="p-2 font-mono text-xs">{d.txn_ref}</td>
                    <td className="p-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                        d.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                        'bg-amber-500/20 text-amber-300'}`}>{d.status}</span>
                      {d.status === 'rejected' && d.admin_notes && (
                        <div className="text-[10px] text-red-300 mt-0.5">{d.admin_notes}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};



// =================== LIVE TRADING TAB ===================
const LiveTradingTab = ({ readiness, bots, orders, apiKeys, setActiveTab, reload }) => {
  const [switching, setSwitching] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [orderForm, setOrderForm] = useState({ broker: 'zerodha', tradingsymbol: 'RELIANCE', exchange: 'NSE', side: 'BUY', qty: 1, order_type: 'MARKET', product: 'CNC', price: '' });

  if (!readiness) return <div className="text-center text-slate-400 py-10">Loading live trading status…</div>;

  const runTestOrder = async () => {
    if (!window.confirm('🧪 Connection Test\n\nThis will place a tiny ₹1-class LIMIT order on your broker (well below market price, so it WILL NOT fill) and immediately cancel it.\n\nNo real funds are risked. Just verifies end-to-end connectivity.\n\nProceed?')) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/algotrading/live/test-order');
      setTestResult({ ok: true, ...r.data });
      toast.success('Connection verified ✓', { style: { background: '#065f46', color: '#ecfdf5' } });
      reload();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
      toast.error(e.response?.data?.detail || 'Test failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally { setTesting(false); }
  };

  const switchBotMode = async (bot, newMode) => {
    if (!window.confirm(`Switch "${bot.strategy_name}" to ${newMode.toUpperCase()} mode?\n\nBot will be PAUSED. You must start it manually after switching.`)) return;
    setSwitching(bot.id);
    try {
      const r = await api.post(`/algotrading/bots/${bot.id}/switch-mode`, { mode: newMode });
      toast.success(r.data.message, { style: { background: '#065f46', color: '#ecfdf5' } });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Switch failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally { setSwitching(null); }
  };

  const placeOrder = async () => {
    if (!readiness.all_ready) {
      toast.error('Complete the checklist below before placing a live order');
      return;
    }
    if (!orderForm.tradingsymbol || !orderForm.qty) { toast.error('Symbol and Qty required'); return; }
    if (!window.confirm(`⚠️ LIVE ORDER\n\n${orderForm.side} ${orderForm.qty} ${orderForm.tradingsymbol} @ ${orderForm.order_type === 'MARKET' ? 'MARKET' : '₹' + orderForm.price}\nBroker: ${orderForm.broker.toUpperCase()} · ${orderForm.exchange}\n\nThis will execute against REAL FUNDS. Proceed?`)) return;
    setPlacingOrder(true);
    try {
      const endpoint = orderForm.broker === 'binance' ? '/algotrading/live/binance/order' : '/algotrading/live/kite/order';
      const payload = orderForm.broker === 'binance'
        ? { symbol: orderForm.tradingsymbol, side: orderForm.side, type: orderForm.order_type, quantity: parseFloat(orderForm.qty), price: orderForm.price ? parseFloat(orderForm.price) : null }
        : { tradingsymbol: orderForm.tradingsymbol, exchange: orderForm.exchange, transaction_type: orderForm.side, quantity: parseInt(orderForm.qty, 10), order_type: orderForm.order_type, product: orderForm.product, price: orderForm.price ? parseFloat(orderForm.price) : null };
      const r = await api.post(endpoint, payload);
      const ordId = r.data?.order_id || r.data?.broker_order_id || 'submitted';
      toast.success(`✓ Live order placed · ${ordId}`, {
        style: { background: '#065f46', color: '#ecfdf5' },
        action: r.data?.whatsapp_share_url ? {
          label: '📱 WhatsApp',
          onClick: () => window.open(r.data.whatsapp_share_url, '_blank'),
        } : undefined,
        duration: 8000,
      });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Order failed', { style: { background: '#7f1d1d', color: '#fef2f2' } });
    } finally { setPlacingOrder(false); }
  };

  const liveBots = (bots || []).filter(b => b.mode === 'live');
  const paperBots = (bots || []).filter(b => b.mode === 'paper');

  const checklistItems = [
    { key: 'global_live_enabled', label: 'Live trading enabled by admin', ok: readiness.global_live_enabled, fix: 'Contact admin to enable', testid: 'chk-global' },
    { key: 'no_kill_switch', label: 'No global kill-switch active', ok: !readiness.kill_switch_active, fix: readiness.kill_switch_reason, testid: 'chk-kill' },
    { key: 'has_sub', label: `Active paid subscription${readiness.sub_tier ? ` (${readiness.sub_tier})` : ''}`, ok: readiness.has_active_subscription && readiness.sub_tier !== 'free_trial', fix: 'Buy a plan (Free Trial is paper-only)', cta: 'plans', testid: 'chk-sub' },
    { key: 'has_key', label: `Validated broker API key${readiness.api_key_broker ? ` (${readiness.api_key_broker})` : ''}`, ok: readiness.has_validated_api_key, fix: 'Add & validate keys in API Vault', cta: 'apikeys', testid: 'chk-key' },
  ];

  return (
    <div className="space-y-5" data-testid="live-trading-tab">
      {/* Hero status banner */}
      <div className={`rounded-2xl p-5 border-2 ${readiness.all_ready ? 'bg-gradient-to-br from-emerald-500/20 to-green-600/10 border-emerald-500/40' : 'bg-gradient-to-br from-amber-500/15 to-orange-600/10 border-amber-500/40'}`} data-testid="live-status-banner">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className={`text-xs font-bold uppercase tracking-widest ${readiness.all_ready ? 'text-emerald-300' : 'text-amber-300'} flex items-center gap-1.5`}>
              <span className={`w-2 h-2 rounded-full ${readiness.all_ready ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`} />
              {readiness.all_ready ? 'LIVE TRADING — READY' : 'LIVE TRADING — NOT READY'}
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold mt-1">
              {readiness.all_ready ? '🔴 Bots trade on YOUR real broker account' : '🛠️ Complete the checklist below'}
            </h2>
            <p className="text-sm text-slate-300 mt-1">
              {readiness.all_ready
                ? <>Orders fire on <b>{readiness.api_key_broker?.toUpperCase()}</b> using <b>your</b> API keys. We never custody funds — you keep full control via your broker dashboard. Bot scheduler ticks <b>every 60 seconds</b> and evaluates signals on each active LIVE bot.</>
                : 'Once all 4 items below show ✓, the active LIVE bots will start firing orders directly to your broker (Zerodha for stocks, Binance for crypto) on each scheduler tick.'}
            </p>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="live-checklist">
        <h3 className="text-lg font-bold mb-3">🚀 Pre-Flight Checklist</h3>
        <div className="space-y-2">
          {checklistItems.map(item => (
            <div key={item.key} className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${item.ok ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`} data-testid={item.testid}>
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${item.ok ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                  {item.ok ? '✓' : '✕'}
                </div>
                <div>
                  <div className={`text-sm font-semibold ${item.ok ? 'text-emerald-200' : 'text-red-200'}`}>{item.label}</div>
                  {!item.ok && item.fix && <div className="text-[11px] text-slate-400 mt-0.5">{item.fix}</div>}
                </div>
              </div>
              {!item.ok && item.cta && (
                <button onClick={() => setActiveTab(item.cta)} className="px-3 py-1.5 bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 rounded-lg text-xs font-bold whitespace-nowrap">
                  Fix Now →
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Connection Smoke-Test — only when checklist is complete */}
      {readiness.all_ready && (
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-5" data-testid="live-test-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <h3 className="text-lg font-bold flex items-center gap-2">
                🧪 Connection Smoke-Test
                <span className="px-2 py-0.5 rounded-full bg-blue-500/30 text-blue-200 text-[10px] font-bold uppercase">Recommended First</span>
              </h3>
              <p className="text-sm text-slate-300 mt-1">
                Place a tiny LIMIT order on your broker (~30% below market — <b>won{`'`}t fill</b>) and auto-cancel within 2 seconds.
                Verifies API keys + broker reachability + order submission + cancel — all without risking real funds.
              </p>
              <p className="text-[11px] text-amber-200/80 mt-1">
                ⚠️ Currently supports Zerodha (Kite) connections. Use sandbox/personal account for first run.
              </p>
            </div>
            <button onClick={runTestOrder} disabled={testing}
              className="px-5 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 active:scale-95 transition disabled:opacity-50 whitespace-nowrap"
              data-testid="run-test-order-btn">
              {testing ? '🔍 Testing…' : '🧪 Run Test Order'}
            </button>
          </div>

          {testResult && (
            <div className={`mt-4 rounded-xl p-4 border ${testResult.ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100' : 'bg-red-500/10 border-red-500/30 text-red-100'}`} data-testid="test-result">
              {testResult.ok ? (
                <>
                  <div className="font-bold flex items-center gap-2">✓ Connection verified</div>
                  <div className="text-sm mt-2 grid sm:grid-cols-2 gap-1">
                    <div>Symbol: <b>{testResult.symbol}</b></div>
                    <div>LTP: <b>₹{testResult.ltp?.toFixed(2)}</b></div>
                    <div>Test limit: <b>₹{testResult.limit_price?.toFixed(2)}</b></div>
                    <div>Broker order ID: <span className="font-mono text-xs">{testResult.order_id}</span></div>
                    <div>Auto-cancelled: <b>{testResult.auto_cancelled ? 'Yes ✓' : 'No ⚠️'}</b></div>
                  </div>
                  {!testResult.auto_cancelled && (
                    <div className="mt-2 text-xs text-amber-300">⚠️ Could not auto-cancel: {testResult.cancel_error}. Please cancel manually in your Kite dashboard.</div>
                  )}
                  <div className="text-xs text-emerald-300/80 mt-2">{testResult.message}</div>
                  {testResult.whatsapp_share_url && (
                    <a href={testResult.whatsapp_share_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/40 text-green-200 rounded-lg text-xs font-bold transition"
                       data-testid="test-result-wa-share">
                      📱 Forward to WhatsApp
                    </a>
                  )}
                </>
              ) : (
                <>
                  <div className="font-bold">✕ Test Failed</div>
                  <div className="text-sm mt-1">{testResult.error}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Live Bots */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="live-bots-section">
        <h3 className="text-lg font-bold mb-3">⚡ Your Live Bots ({liveBots.length})</h3>
        {liveBots.length === 0 ? (
          <div className="bg-slate-800/40 rounded-xl p-4 text-center text-slate-400 text-sm">
            No bots are running in LIVE mode yet.{paperBots.length > 0 && ' Switch a paper bot below to LIVE.'}
          </div>
        ) : (
          <div className="space-y-2">
            {liveBots.map(b => (
              <div key={b.id} className="bg-slate-800/40 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`live-bot-${b.id}`}>
                <div>
                  <div className="font-bold flex items-center gap-2">
                    {b.strategy_name}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200 font-bold">LIVE</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${b.is_active ? 'bg-emerald-500/30 text-emerald-200' : 'bg-slate-600/30 text-slate-400'}`}>{b.is_active ? 'RUNNING' : 'PAUSED'}</span>
                  </div>
                  <div className="text-xs text-slate-400">Asset: {b.asset_class} · Capital: ₹{b.capital_allocated?.toLocaleString('en-IN')} · Trades: {b.total_trades || 0}</div>
                </div>
                <button onClick={() => switchBotMode(b, 'paper')} disabled={switching === b.id}
                  className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/40 text-amber-200 rounded-lg text-xs font-bold disabled:opacity-50"
                  data-testid={`switch-to-paper-${b.id}`}>
                  {switching === b.id ? '…' : '↩ Switch to Paper'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Paper bots that can be promoted to live */}
        {paperBots.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Promote a Paper Bot to LIVE</h4>
            <div className="space-y-2">
              {paperBots.map(b => (
                <div key={b.id} className="bg-slate-800/40 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`promote-${b.id}`}>
                  <div>
                    <div className="font-semibold">{b.strategy_name}</div>
                    <div className="text-xs text-slate-400">{b.asset_class} · ₹{b.capital_allocated?.toLocaleString('en-IN')}</div>
                  </div>
                  <button onClick={() => switchBotMode(b, 'live')} disabled={switching === b.id || !readiness.all_ready}
                    title={!readiness.all_ready ? 'Complete checklist first' : ''}
                    className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-200 rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`switch-to-live-${b.id}`}>
                    {switching === b.id ? '…' : '🔴 Go LIVE'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Manual Live Order */}
      {readiness.all_ready && (
        <div className="bg-white/5 border-2 border-red-500/30 rounded-2xl p-5" data-testid="live-order-form">
          <h3 className="text-lg font-bold mb-1 flex items-center gap-2">🔴 Manual Live Order</h3>
          <p className="text-xs text-red-300/80 mb-4">⚠️ Real money. Double-check every field. No paper simulation.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Broker</label>
              <select value={orderForm.broker} onChange={e => setOrderForm({ ...orderForm, broker: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="lo-broker">
                <option value="zerodha">Zerodha (Kite)</option>
                <option value="binance">Binance (Crypto)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Symbol</label>
              <input value={orderForm.tradingsymbol} onChange={e => setOrderForm({ ...orderForm, tradingsymbol: e.target.value.toUpperCase() })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder={orderForm.broker === 'binance' ? 'BTCUSDT' : 'RELIANCE'} data-testid="lo-symbol" />
            </div>
            {orderForm.broker === 'zerodha' && (
              <div>
                <label className="block text-xs text-slate-400 mb-1">Exchange</label>
                <select value={orderForm.exchange} onChange={e => setOrderForm({ ...orderForm, exchange: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="lo-exchange">
                  <option value="NSE">NSE</option>
                  <option value="BSE">BSE</option>
                  <option value="NFO">NFO (F&O)</option>
                  <option value="MCX">MCX</option>
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setOrderForm({ ...orderForm, side: 'BUY' })} className={`py-2.5 rounded-lg font-bold text-sm transition ${orderForm.side === 'BUY' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'}`} data-testid="lo-buy">BUY</button>
              <button onClick={() => setOrderForm({ ...orderForm, side: 'SELL' })} className={`py-2.5 rounded-lg font-bold text-sm transition ${orderForm.side === 'SELL' ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400'}`} data-testid="lo-sell">SELL</button>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Quantity</label>
              <input type="number" value={orderForm.qty} onChange={e => setOrderForm({ ...orderForm, qty: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="lo-qty" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Order Type</label>
              <select value={orderForm.order_type} onChange={e => setOrderForm({ ...orderForm, order_type: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="lo-otype">
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
              </select>
            </div>
            {orderForm.order_type === 'LIMIT' && (
              <div>
                <label className="block text-xs text-slate-400 mb-1">Limit Price</label>
                <input type="number" step="0.01" value={orderForm.price} onChange={e => setOrderForm({ ...orderForm, price: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="lo-price" />
              </div>
            )}
            {orderForm.broker === 'zerodha' && (
              <div>
                <label className="block text-xs text-slate-400 mb-1">Product</label>
                <select value={orderForm.product} onChange={e => setOrderForm({ ...orderForm, product: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="lo-product">
                  <option value="CNC">CNC (Delivery)</option>
                  <option value="MIS">MIS (Intraday)</option>
                  <option value="NRML">NRML (F&O)</option>
                </select>
              </div>
            )}
          </div>
          <button onClick={placeOrder} disabled={placingOrder}
            className={`w-full mt-4 py-3.5 rounded-xl font-extrabold text-base text-white transition active:scale-95 disabled:opacity-50 ${orderForm.side === 'BUY' ? 'bg-gradient-to-r from-emerald-500 to-green-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}
            data-testid="lo-submit">
            {placingOrder ? 'Placing…' : `🔴 PLACE LIVE ${orderForm.side} ORDER`}
          </button>
        </div>
      )}

      {/* Live Orders History */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="live-orders-history">
        <h3 className="text-lg font-bold mb-3">📋 Live Orders History</h3>
        {orders.length === 0 ? (
          <div className="text-center text-slate-500 py-6 text-sm">No live orders placed yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-white/5 text-xs uppercase text-slate-400">
                <tr><th className="p-2 text-left">Time</th><th className="p-2">Broker</th><th className="p-2 text-left">Symbol</th><th className="p-2">Side</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-center">Status</th></tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id || o.order_id} className="border-t border-white/5">
                    <td className="p-2 text-xs">{new Date(o.created_at || o.timestamp).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="p-2 text-center text-xs uppercase">{o.broker}</td>
                    <td className="p-2 font-bold">{o.tradingsymbol || o.symbol}</td>
                    <td className="p-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${String(o.transaction_type || o.side).toUpperCase() === 'BUY' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                        {String(o.transaction_type || o.side).toUpperCase()}
                      </span>
                    </td>
                    <td className="p-2 text-right">{o.quantity || o.qty}</td>
                    <td className="p-2 text-right">{o.price ? `₹${o.price}` : '—'}</td>
                    <td className="p-2 text-center"><span className="text-xs uppercase">{o.status || 'submitted'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Helpful info */}
      <div className="bg-slate-900/50 border border-blue-500/20 rounded-xl p-4 text-xs text-slate-300 space-y-1">
        <div className="font-bold text-blue-300 mb-1">📖 How Live Trading Works</div>
        <div>1. Buy any paid subscription (Monthly Starter ₹999+).</div>
        <div>2. Add your broker API keys in <button onClick={() => setActiveTab('apikeys')} className="text-blue-400 underline">API Vault</button> and click <b>Validate</b>.</div>
        <div>3. Promote any paper bot to LIVE here, or use the Manual Live Order panel above.</div>
        <div>4. Real funds + real exchange execution. All trades logged in history.</div>
        <div className="mt-2 text-amber-200">⚠️ Binance is geo-blocked for many cloud regions — use VPN or pick WazirX/Delta/Bybit/Zerodha if validation fails.</div>
      </div>
    </div>
  );
};





// ============ AI SMART SIGNALS TAB ============
const SmartSignalsTab = ({ mySubscription, setActiveTab }) => {
  const [categories, setCategories] = useState([]);
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [locked, setLocked] = useState(false);
  const [unlockReq, setUnlockReq] = useState(null);
  const [activeCat, setActiveCat] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [activeCat, activeStatus]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/algotrading/smart-signals/categories'),
        api.get('/algotrading/smart-signals', {
          params: {
            ...(activeCat !== 'all' ? { category: activeCat } : {}),
            ...(activeStatus !== 'all' ? { status: activeStatus.toUpperCase() } : {}),
          }
        }),
      ]);
      setCategories(c.data.categories || []);
      setSignals(s.data.signals || []);
      setStats(s.data.stats || null);
      setLocked(!!s.data.locked);
      setUnlockReq(s.data.unlock_required || null);
    } catch (e) { console.warn(e?.message); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="smart-signals-tab" className="space-y-5">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-pink-950/40 p-5">
        <div className="hidden md:block absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-amber-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 animate-pulse" /> AI-Curated · Updated daily
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-400 to-pink-400 bg-clip-text text-transparent">
              AI SMART SIGNALS
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm mt-1">9 categories · BUY/SELL with TP &amp; SL · Live win-rate tracking</p>
          </div>
          {stats && !locked && (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-xl px-3 py-2">
                <div className="text-[10px] text-emerald-300 uppercase font-semibold">Open</div>
                <div className="text-lg font-extrabold text-emerald-200">{stats.total_open || 0}</div>
              </div>
              <div className="bg-amber-500/15 border border-amber-500/30 rounded-xl px-3 py-2">
                <div className="text-[10px] text-amber-300 uppercase font-semibold">Win Rate</div>
                <div className="text-lg font-extrabold text-amber-200">{stats.win_rate_pct || 0}%</div>
              </div>
              <div className="bg-pink-500/15 border border-pink-500/30 rounded-xl px-3 py-2">
                <div className="text-[10px] text-pink-300 uppercase font-semibold">Closed</div>
                <div className="text-lg font-extrabold text-pink-200">{stats.total_closed || 0}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Paywall Banner */}
      {locked && unlockReq && (
        <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-pink-500/20 border-2 border-amber-400/50 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="smart-signals-paywall">
          <Lock className="w-10 h-10 text-amber-300 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-amber-200 font-bold text-base sm:text-lg">{unlockReq.message}</div>
            <div className="text-amber-200/70 text-xs sm:text-sm mt-1">
              Showing {unlockReq.demo_visible} sample signal(s) — subscribe to unlock unlimited live signals across all 9 categories.
            </div>
          </div>
          <button
            onClick={() => setActiveTab('plans')}
            data-testid="smart-signals-cta-subscribe"
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg shadow-amber-500/40 hover:shadow-amber-500/60 transition whitespace-nowrap"
          >
            {unlockReq.cta || 'Subscribe Now'}
          </button>
        </div>
      )}

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        <button
          onClick={() => setActiveCat('all')}
          data-testid="signal-cat-all"
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
            activeCat === 'all' ? 'bg-amber-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'
          }`}
        >All Categories</button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCat(cat.id)}
            data-testid={`signal-cat-${cat.id}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-1 ${
              activeCat === cat.id ? `bg-gradient-to-r ${cat.color} text-white` : 'bg-white/5 text-slate-300 hover:bg-white/10'
            }`}
          >
            <span>{cat.icon}</span> {cat.name}
          </button>
        ))}
      </div>

      {/* Status filter */}
      <div className="flex gap-2">
        {['all', 'OPEN', 'CLOSED'].map(s => (
          <button
            key={s}
            onClick={() => setActiveStatus(s === 'all' ? 'all' : s.toLowerCase())}
            data-testid={`signal-status-${s.toLowerCase()}`}
            className={`px-3 py-1 rounded-md text-xs font-medium ${
              (activeStatus === 'all' ? 'all' : activeStatus.toUpperCase()) === s
                ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >{s === 'all' ? 'All' : s}</button>
        ))}
      </div>

      {/* Signal cards */}
      {signals.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white/5 border border-white/10 rounded-2xl">
          <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <div className="font-semibold">No signals available yet for this filter.</div>
          <div className="text-xs mt-1">Check back soon — our analysts post new signals daily.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {signals.map(sig => {
            const cat = categories.find(c => c.id === sig.category);
            return (
              <div
                key={sig.id}
                data-testid={`signal-card-${sig.id}`}
                className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950 hover:border-amber-400/50 hover:shadow-lg hover:shadow-amber-500/20 transition group"
              >
                {sig.image_url && (
                  <div className="aspect-video bg-slate-800 overflow-hidden">
                    <img src={sig.image_url} alt={sig.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                  </div>
                )}
                <div className="p-4 space-y-3">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {cat && (
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-gradient-to-r ${cat.color} text-white`}>
                            {cat.icon} {cat.name}
                          </span>
                        )}
                        {sig.is_demo && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">DEMO</span>
                        )}
                      </div>
                      <h3 className="font-bold text-white text-base leading-tight">{sig.title}</h3>
                      <div className="text-xs text-slate-400 mt-0.5">{sig.symbol}</div>
                    </div>
                    {/* Status / Direction */}
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-xs font-extrabold px-2 py-0.5 rounded ${
                        sig.direction === 'BUY' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-red-500/20 text-red-300 border border-red-500/40'
                      }`}>
                        {sig.direction}
                      </span>
                      {sig.status === 'OPEN' ? (
                        <span data-testid={`signal-status-badge-${sig.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/30 text-emerald-200 border border-emerald-400/50 animate-pulse">
                          ● SIGNAL OPENED
                        </span>
                      ) : (
                        <span data-testid={`signal-status-badge-${sig.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          sig.result === 'SUCCESS'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                            : 'bg-red-500/20 text-red-300 border border-red-500/40'
                        }`}>
                          ● CLOSED {sig.result === 'SUCCESS' ? '✓ SUCCESS' : sig.result === 'FAIL' ? '✗ FAIL' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Levels */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg py-1.5 px-2">
                      <div className="text-[9px] uppercase text-blue-300 font-semibold">Entry</div>
                      <div className="text-xs font-bold text-blue-200 truncate">{sig.buy_sell_around}</div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg py-1.5 px-2">
                      <div className="text-[9px] uppercase text-emerald-300 font-semibold">Target</div>
                      <div className="text-xs font-bold text-emerald-200 truncate">{sig.target_price}</div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg py-1.5 px-2">
                      <div className="text-[9px] uppercase text-red-300 font-semibold">SL</div>
                      <div className="text-xs font-bold text-red-200 truncate">{sig.stop_loss}</div>
                    </div>
                  </div>

                  {/* Remarks */}
                  {sig.remarks && (
                    <div className="text-xs text-slate-300 bg-white/5 rounded-lg p-2 border border-white/5">
                      <span className="text-amber-300 font-semibold">Note: </span>{sig.remarks}
                    </div>
                  )}

                  {/* Safety Disclaimer */}
                  {sig.safety_disclaimer && (
                    <div className="text-[10px] text-amber-200/70 italic border-t border-amber-500/20 pt-2 flex items-start gap-1">
                      <ShieldCheck className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{sig.safety_disclaimer}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlgoTrading;

// ============ Clickwrap Consent Modal ============
const ConsentModal = ({ data, onAccept }) => {
  const [scrolled, setScrolled] = useState(false);
  const scrollRef = useRef(null);

  // Auto-enable Accept if content fits without scrolling (short text or tall viewport)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Wait a frame for dangerouslySetInnerHTML content to layout
    const t = setTimeout(() => {
      if (el.scrollHeight - el.clientHeight <= 20) {
        setScrolled(true);
      }
    }, 100);
    return () => clearTimeout(t);
  }, [data]);

  const onScroll = (e) => {
    const t = e.target;
    if (t.scrollTop + t.clientHeight >= t.scrollHeight - 20) setScrolled(true);
  };
  // Convert markdown-ish ** ** to bold
  const html = (data.consent_text || '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n\n/g, '<br/><br/>');
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" data-testid="consent-modal">
      <div className="bg-slate-900 border border-amber-400/40 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-400" />
          <h3 className="font-bold text-white">Consent &amp; Risk Acknowledgement ({data.consent_version})</h3>
        </div>
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-5 text-sm text-slate-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />
        <div className="p-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-400">{scrolled ? '✓ You have read the agreement' : 'Please scroll to the bottom to enable Accept'}</div>
          <button
            onClick={onAccept}
            disabled={!scrolled}
            data-testid="consent-accept-btn"
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" /> I Understand &amp; Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ AI STRATEGY MATCHMAKER TAB ============
const StrategyMatchmakerTab = () => {
  const [step, setStep] = useState('intro'); // 'intro' | 'form' | 'result'
  const [latest, setLatest] = useState(null);
  const [form, setForm] = useState({
    risk_appetite: 'medium',
    investment_horizon: 'swing',
    capital_inr: 100000,
    preferred_markets: ['equity'],
    experience: 'intermediate',
    target_monthly_return_pct: 5,
    can_handle_max_drawdown_pct: 10,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/algotrading/strategy-matchmaker/latest').then(r => {
      if (r.data.has_match) {
        setLatest(r.data.match);
        setResult(r.data.match.result);
      }
    }).catch(() => { });
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/algotrading/strategy-matchmaker', form);
      setResult(r.data);
      setStep('result');
      toast.success('Your personalised strategy plan is ready!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Match failed');
    } finally { setBusy(false); }
  };

  const toggleMarket = (m) => {
    setForm(f => ({
      ...f,
      preferred_markets: f.preferred_markets.includes(m)
        ? f.preferred_markets.filter(x => x !== m)
        : [...f.preferred_markets, m],
    }));
  };

  return (
    <div data-testid="matchmaker-tab" className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-purple-400/40 bg-gradient-to-br from-purple-950/40 via-slate-900 to-indigo-950/40 p-6">
        <div className="hidden md:block absolute -top-12 -right-12 w-48 h-48 rounded-full bg-purple-400/20 blur-3xl" />
        <div className="relative">
          <div className="text-purple-300 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Brain className="w-4 h-4" /> Personalised AI Match</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-purple-300 via-indigo-300 to-pink-300 bg-clip-text text-transparent mt-1">AI Strategy Matchmaker</h2>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">Answer 7 quick questions. Our AI ranks the top 3 algorithmic strategies that match YOUR risk profile, capital, and goals — with GPT-4o explanation.</p>
        </div>
      </div>

      {step === 'intro' && (
        <div className="flex flex-col gap-4">
          {result && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-sm text-emerald-200">
              ✓ You have a previous match. <button onClick={() => setStep('result')} className="underline font-semibold">View result</button> or take the quiz again below.
            </div>
          )}
          <button onClick={() => setStep('form')} data-testid="matchmaker-start-btn" className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold w-fit flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Take the 60-second Quiz <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {step === 'form' && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <RadioRow label="What's your risk appetite?" name="risk" value={form.risk_appetite} options={['low', 'medium', 'high']} onChange={v => setForm(f => ({ ...f, risk_appetite: v }))} />
          <RadioRow label="Investment horizon?" name="horizon" value={form.investment_horizon} options={['intraday', 'swing', 'positional', 'longterm']} onChange={v => setForm(f => ({ ...f, investment_horizon: v }))} />
          <div>
            <label className="text-xs uppercase font-bold text-slate-300 tracking-wider mb-2 block">Trading Capital (₹)</label>
            <input type="number" min="5000" value={form.capital_inr} onChange={e => setForm(f => ({ ...f, capital_inr: parseFloat(e.target.value) || 0 }))} data-testid="matchmaker-capital" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-slate-300 tracking-wider mb-2 block">Preferred Markets (multi-select)</label>
            <div className="flex flex-wrap gap-2">
              {['equity', 'fno', 'crypto', 'forex', 'commodity'].map(m => (
                <button key={m} onClick={() => toggleMarket(m)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${form.preferred_markets.includes(m) ? 'bg-purple-500 text-white' : 'bg-white/5 text-slate-300'}`}>{m}</button>
              ))}
            </div>
          </div>
          <RadioRow label="Experience level?" name="exp" value={form.experience} options={['beginner', 'intermediate', 'expert']} onChange={v => setForm(f => ({ ...f, experience: v }))} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase font-bold text-slate-300 tracking-wider mb-2 block">Target Monthly Return %</label>
              <input type="number" min="1" max="50" value={form.target_monthly_return_pct} onChange={e => setForm(f => ({ ...f, target_monthly_return_pct: parseFloat(e.target.value) || 0 }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-slate-300 tracking-wider mb-2 block">Max Drawdown Tolerance %</label>
              <input type="number" min="1" max="50" value={form.can_handle_max_drawdown_pct} onChange={e => setForm(f => ({ ...f, can_handle_max_drawdown_pct: parseFloat(e.target.value) || 0 }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setStep('intro')} className="px-4 py-2 rounded-lg bg-white/5">Back</button>
            <button onClick={submit} disabled={busy} data-testid="matchmaker-submit-btn" className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold disabled:opacity-50 flex items-center gap-1">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
              {busy ? 'Analyzing…' : 'Get My Match'}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div className="space-y-4" data-testid="matchmaker-result">
          <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border border-emerald-400/30 rounded-2xl p-5">
            <div className="text-emerald-300 text-xs uppercase font-bold tracking-wider mb-1">Recommended Tier</div>
            <div className="text-2xl font-extrabold text-emerald-200">{result.suggested_tier}</div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {result.top_strategies.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-xs uppercase font-bold text-purple-300 tracking-wider">#{i + 1} · score {s.score}</div>
                <div className="text-white font-bold text-lg mt-1">{s.name}</div>
                <div className="text-xs text-slate-300 mt-2">{s.details?.description || s.details?.summary || ''}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {s.details?.risk && <span className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded">Risk: {s.details.risk}</span>}
                  {(s.details?.time_horizon || s.details?.horizon) && <span className="text-[10px] bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">{s.details.time_horizon || s.details.horizon}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-purple-500/10 border border-purple-400/30 rounded-2xl p-5">
            <div className="text-purple-300 text-xs uppercase font-bold tracking-wider mb-2 flex items-center gap-1"><Brain className="w-3.5 h-3.5" /> AI Explanation</div>
            <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-line">{result.ai_explanation}</div>
          </div>
          <button onClick={() => { setStep('form'); }} className="px-5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm">Retake Quiz</button>
        </div>
      )}
    </div>
  );
};

const RadioRow = ({ label, value, options, onChange, name }) => (
  <div>
    <label className="text-xs uppercase font-bold text-slate-300 tracking-wider mb-2 block">{label}</label>
    <div className="flex gap-2 flex-wrap">
      {options.map(o => (
        <button key={o} onClick={() => onChange(o)} data-testid={`mm-${name}-${o}`} className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${value === o ? 'bg-purple-500 text-white' : 'bg-white/5 text-slate-300'}`}>{o}</button>
      ))}
    </div>
  </div>
);

// ============ LIVE BROKER DASHBOARD TAB ============
const LiveBrokerDashboardTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panicLoading, setPanicLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/algotrading/live/dashboard');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const panicClose = async () => {
    if (!window.confirm('⚠ PANIC BUTTON\n\nThis will SQUARE-OFF ALL open positions across all brokers at MARKET price.\n\nAre you absolutely sure?')) return;
    setPanicLoading(true);
    try {
      const r = await api.post('/algotrading/live/panic-close-all');
      toast.success(`Closed ${r.data.total_closed} positions. ${r.data.errors.length ? r.data.errors.length + ' errors.' : ''}`);
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Panic failed');
    } finally { setPanicLoading(false); }
  };

  if (loading) return <div className="text-center py-12"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-emerald-400" /></div>;

  return (
    <div data-testid="live-dashboard-tab" className="space-y-5">
      {/* Master Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-4 text-white">
          <div className="text-xs uppercase opacity-80">Total Cash</div>
          <div className="text-xl font-extrabold">₹{(data?.total_cash || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className={`bg-gradient-to-br rounded-2xl p-4 text-white ${(data?.total_m2m || 0) >= 0 ? 'from-blue-500 to-cyan-500' : 'from-red-500 to-orange-500'}`}>
          <div className="text-xs uppercase opacity-80">M2M P&amp;L</div>
          <div className="text-xl font-extrabold">{(data?.total_m2m || 0) >= 0 ? '+' : ''}₹{(data?.total_m2m || 0).toFixed(2)}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-4 text-white">
          <div className="text-xs uppercase opacity-80">Open Positions</div>
          <div className="text-xl font-extrabold">{data?.total_positions || 0}</div>
        </div>
        <button
          onClick={panicClose}
          disabled={!data?.panic_button_eligible || panicLoading}
          data-testid="panic-button"
          className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-4 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:from-red-500 hover:to-red-700 transition flex items-center justify-center gap-2"
        >
          <AlertCircle className="w-6 h-6" />
          <div>
            <div className="text-xs uppercase">Panic</div>
            <div className="text-sm font-extrabold">CLOSE ALL</div>
          </div>
        </button>
      </div>

      {/* Per-broker breakdown */}
      <div className="grid lg:grid-cols-2 gap-4">
        {(data?.brokers || []).map((b, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`broker-card-${b.broker}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-bold text-white text-lg capitalize flex items-center gap-2">{b.broker}
                {b.connected ? <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded">✓ Connected</span> : <span className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded">Not connected</span>}
              </div>
              {b.is_testnet && <span className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">TESTNET</span>}
            </div>
            {b.connected && (
              <>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2">
                    <div className="text-[10px] text-emerald-300 uppercase">Cash</div>
                    <div className="text-base font-bold text-emerald-200">₹{((b.cash_available || b.cash_available_usdt) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}{b.broker === 'binance' ? ' USDT' : ''}</div>
                  </div>
                  {typeof b.m2m_pnl !== 'undefined' && (
                    <div className={`border rounded-lg p-2 ${b.m2m_pnl >= 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                      <div className={`text-[10px] uppercase ${b.m2m_pnl >= 0 ? 'text-blue-300' : 'text-red-300'}`}>M2M</div>
                      <div className={`text-base font-bold ${b.m2m_pnl >= 0 ? 'text-blue-200' : 'text-red-200'}`}>{b.m2m_pnl >= 0 ? '+' : ''}₹{b.m2m_pnl.toFixed(2)}</div>
                    </div>
                  )}
                </div>
                {b.open_positions && b.open_positions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-slate-400 border-b border-white/10">
                        <tr><th className="text-left py-1">Symbol</th><th className="text-right">Qty</th><th className="text-right">Avg</th><th className="text-right">LTP</th><th className="text-right">P&amp;L</th></tr>
                      </thead>
                      <tbody>
                        {b.open_positions.map((p, idx) => (
                          <tr key={idx} className="border-b border-white/5">
                            <td className="py-1 text-white font-semibold">{p.symbol}</td>
                            <td className="text-right">{p.qty}</td>
                            <td className="text-right text-slate-300">{p.avg_price.toFixed(2)}</td>
                            <td className="text-right text-amber-300">{p.ltp.toFixed(2)}</td>
                            <td className={`text-right font-bold ${p.pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{p.pnl.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">No open positions</div>
                )}
              </>
            )}
            {!b.connected && (
              <div className="text-sm text-slate-400">{b.error || 'Connect this broker in the API Vault tab to see live data.'}</div>
            )}
          </div>
        ))}
      </div>
      <div className="text-xs text-slate-500 flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Auto-refreshes every 30s · Last updated {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
};

// ============ PROFIT SHARE TAB ============
const ProfitShareTab = ({ paperWallet: _pw }) => {
  const [status, setStatus] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupAmt, setTopupAmt] = useState(500);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get('/algotrading/profit-share/status'),
        api.get('/algotrading/profit-share/ledger?limit=30'),
      ]);
      setStatus(s.data);
      setLedger(l.data.entries || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const enroll = async () => {
    setBusy(true);
    try {
      await api.post('/algotrading/profit-share/enroll', { profit_share_pct: 20, auto_charge_enabled: true });
      toast.success('Enrolled into 20% Profit-Share!');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Enroll failed'); }
    finally { setBusy(false); }
  };

  const topup = async () => {
    if (topupAmt <= 0) return;
    setBusy(true);
    try {
      const r = await api.post('/algotrading/profit-share/topup', { amount_inr: topupAmt, payment_method: 'wallet' });
      toast.success(`Topped up ₹${topupAmt}. Prepaid: ₹${r.data.new_prepaid_balance}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Top-up failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-12"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-400" /></div>;

  return (
    <div data-testid="profit-share-tab" className="space-y-5">
      <div className="relative overflow-hidden rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-950/40 via-slate-900 to-teal-950/40 p-6">
        <div className="hidden md:block absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="relative">
          <div className="text-emerald-300 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><IndianRupee className="w-4 h-4" /> Performance-Linked Pricing</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent mt-1">20% Profit-Share Model</h2>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">
            Pay only when you profit. We charge 20% of new profit ABOVE your High-Water Mark (HWM). Fees auto-deduct from your Prepaid Wallet. Zero prepaid balance → live algo trading auto-pauses (protects you).
          </p>
        </div>
      </div>

      {!status?.enrolled ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
          <Brain className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
          <div className="text-white font-bold mb-2">Not enrolled yet</div>
          <div className="text-slate-300 text-sm mb-4 max-w-md mx-auto">Enroll to unlock 20% profit-share pricing. Subscription-based pricing remains available — you choose any time.</div>
          <button onClick={enroll} disabled={busy} data-testid="ps-enroll-btn" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold disabled:opacity-50">
            Enroll in 20% Profit-Share
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl p-4 text-white">
              <div className="text-xs uppercase opacity-80">Profit Share</div>
              <div className="text-2xl font-extrabold">{status.profit_share_pct}%</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-4 text-white">
              <div className="text-xs uppercase opacity-80">Prepaid Wallet</div>
              <div className="text-2xl font-extrabold">₹{(status.prepaid_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-4 text-white">
              <div className="text-xs uppercase opacity-80">High-Water Mark</div>
              <div className="text-2xl font-extrabold">₹{(status.high_water_mark || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
            </div>
            <div className={`rounded-xl p-4 text-white bg-gradient-to-br ${status.auto_paused ? 'from-red-500 to-rose-500' : 'from-purple-500 to-pink-500'}`}>
              <div className="text-xs uppercase opacity-80">Status</div>
              <div className="text-sm font-extrabold">{status.auto_paused ? '⏸ AUTO-PAUSED' : '▶ ACTIVE'}</div>
              <div className="text-xs opacity-80 mt-1">Total fees: ₹{status.total_fees_paid || 0}</div>
            </div>
          </div>

          {status.auto_paused && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-bold text-red-200">Auto-trading paused — prepaid wallet is empty</div>
                <div className="text-xs text-red-200/70">Top up to resume live algorithmic trading.</div>
              </div>
            </div>
          )}

          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="text-white font-bold mb-3 flex items-center gap-2"><Wallet className="w-4 h-4" /> Top up Prepaid Wallet</div>
            <div className="flex gap-2 mb-3">
              {[500, 1000, 5000].map(v => (
                <button key={v} onClick={() => setTopupAmt(v)} data-testid={`ps-quick-${v}`} className={`px-3 py-1.5 rounded-lg text-sm ${topupAmt === v ? 'bg-emerald-500 text-white' : 'bg-white/5'}`}>₹{v}</button>
              ))}
              <input type="number" min="1" value={topupAmt} onChange={e => setTopupAmt(parseFloat(e.target.value) || 0)} className="bg-slate-800 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white w-32" />
            </div>
            <button onClick={topup} disabled={busy} data-testid="ps-topup-btn" className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold disabled:opacity-50">
              Top up ₹{topupAmt} from Cash Wallet
            </button>
            <div className="text-xs text-slate-400 mt-2">Funds debit from your existing cash wallet. 1:1 INR.</div>
          </div>

          {ledger.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="p-3 border-b border-white/10 text-white font-bold text-sm">Recent Ledger</div>
              <table className="w-full text-xs">
                <thead><tr className="text-slate-400 bg-white/5"><th className="p-2 text-left">When</th><th className="text-left">Type</th><th className="text-right">Amount</th><th className="text-right">Balance</th></tr></thead>
                <tbody>
                  {ledger.map(l => (
                    <tr key={l.id} className="border-t border-white/5">
                      <td className="p-2">{new Date(l.created_at).toLocaleString()}</td>
                      <td className={l.type === 'TOPUP' ? 'text-emerald-300' : 'text-red-300'}>{l.type}</td>
                      <td className={`text-right ${l.amount >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{l.amount >= 0 ? '+' : ''}₹{Math.abs(l.amount).toFixed(2)}</td>
                      <td className="text-right text-slate-300">₹{l.new_balance?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
